import * as THREE from "three";
import { CSS3DObject, CSS3DRenderer } from "three/examples/jsm/renderers/CSS3DRenderer.js";
import type { Tables } from "@/types/database";
import { useEditorStore } from "@/store/editorStore";
import { useVariablesStore } from "@/store/variablesStore";
import { resolveCSSVariable } from "@/utils/resolveCSSVariables";
import { sanitizeSvgMarkup, sanitizeHotspotHTML } from "@/utils/svgSanitizer";
import { promptAndSaveFile } from "@/utils/fileDownload";
import { resolveTextVariables } from "@/components/editor/resolveTextVariables";
import { icons } from "lucide-react";
import { renderToStaticMarkup } from "react-dom/server";
import {
  clearSVGTextureCache,
  createHotspotIconTexture,
  createSVGTexture,
} from "./HotspotTextureUtils";

const warnSlideToDev = (stage: string, payload: Record<string, unknown>) => {
  if (!import.meta.env.DEV) return;
  console.warn(`[slide-to] ${stage}`, payload);
};

type Hotspot = Tables<"hotspots">;
type Project = Tables<"projects">;
type Panorama = Tables<"panoramas">;
type HotspotContent = Record<string, unknown>;
type HotspotPosition = { x?: number; y?: number; z?: number };
type HotspotScale = { x?: number; y?: number; z?: number };
type HotspotHoverStyle = {
  backgroundColor?: string;
  borderColor?: string;
  color?: string;
  boxShadow?: string;
  textShadow?: string;
  opacity?: string;
  transform?: string;
};
type HotspotHoverElement = HTMLElement & {
  __hotspotBaseStyle?: HotspotHoverStyle;
  __hotspotHoverStyle?: HotspotHoverStyle | null;
  __hotspotHoverHandler?: () => void;
  __hotspotUnhoverHandler?: () => void;
  __hotspotIsHovered?: boolean;
  __hotspotTooltipHoverHandler?: () => void;
  __hotspotTooltipUnhoverHandler?: () => void;
};
type HotspotArrowDirection =
  | "top"
  | "right"
  | "bottom"
  | "left"
  | "top-left"
  | "top-right"
  | "bottom-right"
  | "bottom-left";

const HOTSPOT_ARROW_DIRECTIONS = new Set<HotspotArrowDirection>([
  "top",
  "right",
  "bottom",
  "left",
]);

const resolveArrowDirection = (
  value: unknown,
  fallback: HotspotArrowDirection,
): HotspotArrowDirection => {
  if (typeof value === "string") {
    const normalized = value
      .trim()
      .replace(/([a-z])([A-Z])/g, "$1-$2")
      .replace(/[_\\s]+/g, "-")
      .toLowerCase();
    if (HOTSPOT_ARROW_DIRECTIONS.has(normalized as HotspotArrowDirection)) {
      return normalized as HotspotArrowDirection;
    }
    if (normalized.includes("bottom")) return "bottom";
    if (normalized.includes("top")) return "top";
    if (normalized.includes("right")) return "right";
    if (normalized.includes("left")) return "left";
  }
  return fallback;
};

const applyTriangleBorderStyles = (
  arrow: HTMLElement,
  direction: HotspotArrowDirection,
  color: string,
  size: number,
) => {
  arrow.style.width = "0";
  arrow.style.height = "0";
  arrow.style.borderStyle = "solid";
  arrow.style.borderTopWidth = "0";
  arrow.style.borderRightWidth = "0";
  arrow.style.borderBottomWidth = "0";
  arrow.style.borderLeftWidth = "0";
  arrow.style.borderTopColor = "transparent";
  arrow.style.borderRightColor = "transparent";
  arrow.style.borderBottomColor = "transparent";
  arrow.style.borderLeftColor = "transparent";

  switch (direction) {
    case "top":
      arrow.style.borderLeftWidth = `${size}px`;
      arrow.style.borderRightWidth = `${size}px`;
      arrow.style.borderBottomWidth = `${size}px`;
      arrow.style.borderBottomColor = color;
      break;
    case "bottom":
      arrow.style.borderLeftWidth = `${size}px`;
      arrow.style.borderRightWidth = `${size}px`;
      arrow.style.borderTopWidth = `${size}px`;
      arrow.style.borderTopColor = color;
      break;
    case "left":
      arrow.style.borderTopWidth = `${size}px`;
      arrow.style.borderBottomWidth = `${size}px`;
      arrow.style.borderRightWidth = `${size}px`;
      arrow.style.borderRightColor = color;
      break;
    case "right":
      arrow.style.borderTopWidth = `${size}px`;
      arrow.style.borderBottomWidth = `${size}px`;
      arrow.style.borderLeftWidth = `${size}px`;
      arrow.style.borderLeftColor = color;
      break;
    case "top-left":
    case "top-right":
    case "bottom-right":
    case "bottom-left": {
      arrow.style.borderLeftWidth = `${size}px`;
      arrow.style.borderRightWidth = `${size}px`;
      arrow.style.borderBottomWidth = `${size}px`;
      arrow.style.borderBottomColor = color;
      const rotation =
        direction === "top-left"
          ? "-45deg"
          : direction === "top-right"
            ? "45deg"
            : direction === "bottom-right"
              ? "135deg"
              : "-135deg";
      const current = arrow.style.transform ? `${arrow.style.transform} ` : "";
      arrow.style.transform = `${current}rotate(${rotation})`.trim();
      break;
    }
  }
};

const applyTooltipPlacement = (tooltip: HTMLElement, direction: HotspotArrowDirection, offset: number) => {
  tooltip.style.top = "";
  tooltip.style.bottom = "";
  tooltip.style.left = "";
  tooltip.style.right = "";
  tooltip.style.transform = "";
  tooltip.style.marginTop = "0";
  tooltip.style.marginBottom = "0";
  tooltip.style.marginLeft = "0";
  tooltip.style.marginRight = "0";

  switch (direction) {
    case "top":
    case "top-left":
    case "top-right":
      tooltip.style.top = "100%";
      tooltip.style.left = "50%";
      tooltip.style.transform = "translateX(-50%)";
      tooltip.style.marginTop = `${offset}px`;
      break;
    case "bottom":
    case "bottom-left":
    case "bottom-right":
      tooltip.style.bottom = "100%";
      tooltip.style.left = "50%";
      tooltip.style.transform = "translateX(-50%)";
      tooltip.style.marginBottom = `${offset}px`;
      break;
    case "left":
      tooltip.style.left = "100%";
      tooltip.style.top = "50%";
      tooltip.style.transform = "translateY(-50%)";
      tooltip.style.marginLeft = `${offset}px`;
      break;
    case "right":
      tooltip.style.right = "100%";
      tooltip.style.top = "50%";
      tooltip.style.transform = "translateY(-50%)";
      tooltip.style.marginRight = `${offset}px`;
      break;
  }
};

const applyTooltipArrowStyles = (
  arrow: HTMLElement,
  direction: HotspotArrowDirection,
  color: string,
  size: number,
) => {
  arrow.style.position = "absolute";
  arrow.style.zIndex = "-1";
  arrow.style.top = "";
  arrow.style.bottom = "";
  arrow.style.left = "";
  arrow.style.right = "";
  arrow.style.transformOrigin = "center";

  switch (direction) {
    case "top":
      arrow.style.top = `${-size}px`;
      arrow.style.left = "50%";
      arrow.style.transform = "translateX(-50%)";
      break;
    case "bottom":
      arrow.style.bottom = `${-size}px`;
      arrow.style.left = "50%";
      arrow.style.transform = "translateX(-50%)";
      break;
    case "left":
      arrow.style.left = `${-size}px`;
      arrow.style.top = "50%";
      arrow.style.transform = "translateY(-50%)";
      break;
    case "right":
      arrow.style.right = `${-size}px`;
      arrow.style.top = "50%";
      arrow.style.transform = "translateY(-50%)";
      break;
    case "top-left":
      arrow.style.top = `${-size}px`;
      arrow.style.left = `${-size}px`;
      break;
    case "top-right":
      arrow.style.top = `${-size}px`;
      arrow.style.right = `${-size}px`;
      break;
    case "bottom-right":
      arrow.style.bottom = `${-size}px`;
      arrow.style.right = `${-size}px`;
      break;
    case "bottom-left":
      arrow.style.bottom = `${-size}px`;
      arrow.style.left = `${-size}px`;
      break;
  }

  applyTriangleBorderStyles(arrow, direction, color, size);
};

const applyHotspotPointerStyles = (
  arrow: HTMLElement,
  direction: HotspotArrowDirection,
  color: string,
  size: number,
  overlap: number,
  cornerRadius: number,
  inverseScale?: { x: number; y: number },
) => {
  arrow.style.position = "absolute";
  arrow.style.top = "";
  arrow.style.bottom = "";
  arrow.style.left = "";
  arrow.style.right = "";
  arrow.style.transform = "";
  arrow.style.transformOrigin = "center center";

  const diagonalOffset = Math.max(0, cornerRadius * (1 - 1 / Math.SQRT2));
  const effectiveOverlap = overlap + 0.5;

  switch (direction) {
    case "bottom":
      arrow.style.top = `calc(100% - ${effectiveOverlap}px)`;
      arrow.style.left = "50%";
      arrow.style.transform = "translateX(-50%)";
      arrow.style.transformOrigin = "center top";
      break;
    case "top":
      arrow.style.bottom = `calc(100% - ${effectiveOverlap}px)`;
      arrow.style.left = "50%";
      arrow.style.transform = "translateX(-50%)";
      arrow.style.transformOrigin = "center bottom";
      break;
    case "left":
      arrow.style.right = `calc(100% - ${effectiveOverlap}px)`;
      arrow.style.top = "50%";
      arrow.style.transform = "translateY(-50%)";
      arrow.style.transformOrigin = "right center";
      break;
    case "right":
      arrow.style.left = `calc(100% - ${effectiveOverlap}px)`;
      arrow.style.top = "50%";
      arrow.style.transform = "translateY(-50%)";
      arrow.style.transformOrigin = "left center";
      break;
    case "top-left":
      arrow.style.bottom = `calc(100% - ${effectiveOverlap}px)`;
      arrow.style.left = `${diagonalOffset}px`;
      arrow.style.transform = "translateX(-50%)";
      arrow.style.transformOrigin = "right bottom";
      break;
    case "top-right":
      arrow.style.bottom = `calc(100% - ${effectiveOverlap}px)`;
      arrow.style.left = `calc(100% - ${diagonalOffset}px)`;
      arrow.style.transform = "translateX(-50%)";
      arrow.style.transformOrigin = "left bottom";
      break;
    case "bottom-right":
      arrow.style.top = `calc(100% - ${effectiveOverlap}px)`;
      arrow.style.left = `calc(100% - ${diagonalOffset}px)`;
      arrow.style.transform = "translateX(-50%)";
      arrow.style.transformOrigin = "left top";
      break;
    case "bottom-left":
      arrow.style.top = `calc(100% - ${effectiveOverlap}px)`;
      arrow.style.left = `${diagonalOffset}px`;
      arrow.style.transform = "translateX(-50%)";
      arrow.style.transformOrigin = "right top";
      break;
  }

  if (inverseScale) {
    const current = arrow.style.transform ? `${arrow.style.transform} ` : "";
    arrow.style.transform = `${current}scale(${inverseScale.x}, ${inverseScale.y})`.trim();
  }

  applyTriangleBorderStyles(arrow, direction, color, size);
};

const applyHotspotPointerLineStyles = (
  line: HTMLElement,
  direction: HotspotArrowDirection,
  color: string,
  lineGap: number,
  thickness: number,
) => {
  line.style.position = "absolute";
  line.style.zIndex = "-1";
  line.style.backgroundColor = color;
  line.style.top = "";
  line.style.bottom = "";
  line.style.left = "";
  line.style.right = "";
  line.style.width = "";
  line.style.height = "";
  line.style.transform = "";

  if (direction === "bottom") {
    line.style.left = "50%";
    line.style.top = "50%";
    line.style.width = `${thickness}px`;
    line.style.height = `calc(50% + ${lineGap}px)`;
    line.style.transform = "translateX(-50%)";
  } else if (direction === "top") {
    line.style.left = "50%";
    line.style.bottom = "50%";
    line.style.width = `${thickness}px`;
    line.style.height = `calc(50% + ${lineGap}px)`;
    line.style.transform = "translateX(-50%)";
  } else if (direction === "right") {
    line.style.left = "50%";
    line.style.top = "50%";
    line.style.height = `${thickness}px`;
    line.style.width = `calc(50% + ${lineGap}px)`;
    line.style.transform = "translateY(-50%)";
  } else if (direction === "left") {
    line.style.right = "50%";
    line.style.top = "50%";
    line.style.height = `${thickness}px`;
    line.style.width = `calc(50% + ${lineGap}px)`;
    line.style.transform = "translateY(-50%)";
  }
};

// Keep HTML hotspots above the panorama canvas but below app UI panels/modals.
const HOTSPOT_LAYER_Z_INDEX = 10;

const getHotspotContent = (hotspot: Hotspot): HotspotContent =>
  hotspot.content && typeof hotspot.content === "object" ? (hotspot.content as HotspotContent) : {};

const resolveLinkedPanoramaName = (content: HotspotContent, panoramas: Panorama[]): string => {
  const linkedPanoramaId =
    typeof content.linkedPanoramaId === "string" ? content.linkedPanoramaId : "";
  if (!linkedPanoramaId) return "";
  const linkedPanorama = panoramas.find((panorama) => panorama.id === linkedPanoramaId);
  return linkedPanorama?.name || "";
};

const resolveCurrentPanoramaName = (hotspot: Hotspot, panoramas: Panorama[]): string => {
  const panoramaId =
    (hotspot as { panorama_id?: string; panoramaId?: string }).panorama_id ||
    (hotspot as { panorama_id?: string; panoramaId?: string }).panoramaId;
  if (!panoramaId) return "";
  const panorama = panoramas.find((item) => item.id === panoramaId);
  return panorama?.name || "";
};

const getNavigateTargetPanoramaIds = (content: HotspotContent): string[] => {
  const targets = new Set<string>();
  const actions = Array.isArray(content.actions) ? (content.actions as Array<Record<string, unknown>>) : [];
  for (const action of actions) {
    if ((action?.type as string) === "navigate" && action.linkedPanoramaId) {
      targets.add(String(action.linkedPanoramaId));
    }
  }
  if (content.linkedPanoramaId) {
    targets.add(String(content.linkedPanoramaId));
  }
  return Array.from(targets);
};

const getVisitedPanoramaIds = (panoramas: Panorama[] | undefined): Set<string> => {
  if (!panoramas) return new Set();
  return new Set(
    panoramas
      .filter((panorama) => panorama.isDiscovered)
      .map((panorama) => panorama.id),
  );
};

const getHotspotPosition = (hotspot: Hotspot): HotspotPosition =>
  hotspot.position && typeof hotspot.position === "object"
    ? (hotspot.position as HotspotPosition)
    : {};

const resolveSizeValue = (value: unknown, fallback: string) => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    if (trimmed.startsWith("var(")) return trimmed;
    if (/[a-z%]+$/i.test(trimmed)) return trimmed;
    const numeric = Number(trimmed.replace(",", "."));
    if (!Number.isNaN(numeric)) return `${numeric}px`;
    return fallback;
  }
  if (typeof value === "number" && !Number.isNaN(value)) {
    return `${value}px`;
  }
  return fallback;
};

const resolveIconSizeValue = (
  value: unknown,
  fallback: string,
  baseWidthPx: number | null,
  baseHeightPx: number | null,
): string => {
  const baseSizePx =
    baseWidthPx !== null && baseHeightPx !== null
      ? Math.min(baseWidthPx, baseHeightPx)
      : (baseWidthPx ?? baseHeightPx ?? 48);
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    if (trimmed.startsWith("var(")) return trimmed;
    const percentMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)%$/);
    if (percentMatch) {
      const pct = Number(percentMatch[1]);
      if (!Number.isNaN(pct)) {
        return `${(baseSizePx * pct) / 100}px`;
      }
    }
    if (/[a-z%]+$/i.test(trimmed)) return trimmed;
    const numeric = Number(trimmed.replace(",", "."));
    if (!Number.isNaN(numeric)) return `${numeric}px`;
    return fallback;
  }
  if (typeof value === "number" && !Number.isNaN(value)) {
    return `${value}px`;
  }
  return fallback;
};

const parsePixelValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const match = trimmed.match(/^(-?\d+(?:\.\d+)?)(px)?$/i);
    if (match) {
      const numeric = Number(match[1]);
      return Number.isNaN(numeric) ? null : numeric;
    }
  }
  return null;
};
const resolveNumberValue = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    const parsed = Number.parseFloat(trimmed);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const getHotspotScale = (hotspot: Hotspot): HotspotScale =>
  hotspot.scale && typeof hotspot.scale === "object" ? (hotspot.scale as HotspotScale) : {};

const getNormalizedOpacity = (value: unknown, fallback = 1): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.min(1, Math.max(0, value));
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return Math.min(1, Math.max(0, parsed));
    }
  }
  return fallback;
};

const isSvgMarkup = (value: unknown): value is string =>
  typeof value === "string" && value.trim().startsWith("<svg");

const isImageSource = (value: string): boolean =>
  /^(https?:)?\/\//i.test(value) || /^data:image\//i.test(value);

const resolveLucideIconMarkup = (iconName: string): string => {
  const IconComponent =
    (icons as Record<string, (props: { size?: number; className?: string }) => JSX.Element>)[
      iconName
    ];
  if (!IconComponent) return "";
  return renderToStaticMarkup(<IconComponent size={48} />);
};

const resolveIconVariableValue = (
  iconVariable: string,
  craftspaceId?: string,
  projectId?: string,
): string => {
  if (!iconVariable || !craftspaceId || !projectId) return "";
  const varsState = useVariablesStore.getState();
  const combined = varsState.getCombinedVariables(craftspaceId, projectId).icons;
  const match =
    combined.find((iconVar) => iconVar.name === iconVariable) ||
    combined.find((iconVar) => iconVar.name.toLowerCase() === iconVariable.toLowerCase());
  return match?.value || "";
};

const resolveIconContentValue = (
  content: HotspotContent | undefined,
  craftspaceId?: string,
  projectId?: string,
): string => {
  const iconVariable =
    typeof content?.iconVariable === "string" ? (content.iconVariable as string).trim() : "";
  const variableValue = resolveIconVariableValue(iconVariable, craftspaceId, projectId);
  const raw =
    variableValue ||
    (typeof content?.iconOrSvg === "string" ? (content.iconOrSvg as string).trim() : "");
  if (!raw) return "";
  if (raw.startsWith("lucide:")) {
    const iconName = raw.slice("lucide:".length);
    const svgMarkup = resolveLucideIconMarkup(iconName);
    return svgMarkup || raw;
  }
  return raw;
};

const resolveHotspotPadding = (content?: HotspotContent): string => {
  const baseValue = (content?.padding as number | undefined) ?? 0;
  const topValue = (content?.paddingTop as number | undefined) ?? baseValue;
  const rightValue = (content?.paddingRight as number | undefined) ?? baseValue;
  const bottomValue = (content?.paddingBottom as number | undefined) ?? baseValue;
  const leftValue = (content?.paddingLeft as number | undefined) ?? baseValue;
  const hasPerSide =
    content?.paddingTop !== undefined ||
    content?.paddingRight !== undefined ||
    content?.paddingBottom !== undefined ||
    content?.paddingLeft !== undefined;
  if (!hasPerSide) {
    return `${baseValue}px`;
  }
  return `${topValue}px ${rightValue}px ${bottomValue}px ${leftValue}px`;
};

const clampNumber = (value: unknown, fallback: number, min: number, max: number): number => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number.parseFloat(value)
        : Number.NaN;
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
};

const getVideoSettings = (content: HotspotContent) => ({
  videoUrl: typeof content.videoUrl === "string" ? content.videoUrl.trim() : "",
  videoLoop: content.videoLoop !== false,
  videoMuted: content.videoMuted !== false,
  videoAutoplay: content.videoAutoplay !== false,
  videoWidth: clampNumber(content.videoWidth, 80, 1, 1000),
  videoHeight: clampNumber(content.videoHeight, 45, 1, 1000),
  chromaKeyEnabled: content.chromaKeyEnabled !== false,
  chromaKeyColor: typeof content.chromaKeyColor === "string" ? content.chromaKeyColor : "#00ff00",
  chromaKeySimilarity: clampNumber(content.chromaKeySimilarity, 0.12, 0, 1),
  chromaKeySmoothness: clampNumber(content.chromaKeySmoothness, 0.08, 0, 1),
  chromaKeySpill: clampNumber(content.chromaKeySpill, 0.2, 0, 1),
});

const getHotspotRenderStyle = (
  hotspot: Hotspot,
  fallback: "billboard" | "3d",
): "billboard" | "3d" => {
  const content = getHotspotContent(hotspot) || {};
  const style = (content as Record<string, unknown>).renderStyle;
  return style === "3d" || style === "billboard" ? style : fallback;
};

const createVideoPlaceholderTexture = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 144;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.fillStyle = "#1f2937";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#4b5563";
    ctx.lineWidth = 4;
    ctx.strokeRect(6, 6, canvas.width - 12, canvas.height - 12);
    ctx.fillStyle = "#e5e7eb";
    ctx.font = "bold 18px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Video", canvas.width / 2, canvas.height / 2);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  return texture;
};

const VIDEO_VERTEX_SHADER = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const VIDEO_FRAGMENT_SHADER = `
  uniform sampler2D map;
  uniform vec3 keyColor;
  uniform float similarity;
  uniform float smoothness;
  uniform float spill;
  uniform float useChroma;
  varying vec2 vUv;

  vec3 rgbToYcbcr(vec3 c) {
    float y = dot(c, vec3(0.2126, 0.7152, 0.0722));
    float cb = (c.b - y) * 0.5 + 0.5;
    float cr = (c.r - y) * 0.5 + 0.5;
    return vec3(y, cb, cr);
  }

  void main() {
    vec4 videoColor = texture2D(map, vUv);
    float alpha = videoColor.a;

    if (useChroma > 0.5) {
      vec3 ycbcr = rgbToYcbcr(videoColor.rgb);
      vec3 keyYcbcr = rgbToYcbcr(keyColor);
      float dist = distance(ycbcr.yz, keyYcbcr.yz);
      float mask = smoothstep(similarity, similarity + smoothness, dist);
      alpha *= mask;
      if (spill > 0.0) {
        float maxRB = max(videoColor.r, videoColor.b);
        float spillMix = clamp((videoColor.g - maxRB) / (videoColor.g + 0.001), 0.0, 1.0);
        videoColor.g = mix(videoColor.g, maxRB, spill * spillMix * (1.0 - mask));
      }
    }

    if (alpha < 0.01) discard;
    gl_FragColor = vec4(videoColor.rgb, alpha);
  }
`;

export class HotspotManager {
  private scene: THREE.Scene;
  private css3dScene: THREE.Scene;
  private css3dRenderer: CSS3DRenderer;
  private container: HTMLElement;
  private hotspotMeshes: { [id: string]: THREE.Object3D } = {};
  private htmlHotspots: { [id: string]: CSS3DObject } = {};
  private hotspotStackById: Map<string, number> = new Map();
  private currentProcessVersion: number = 0;
  private isDragging: boolean = false;
  private draggedHotspotId: string | null = null;
  private isPreviewMode: boolean = false;
  private currentRoute: "editor" | "skin" | "cms" | "settings" | "trash" = "editor";
  private renderStyle: "billboard" | "3d" = "billboard";
  private lastCamera: THREE.Camera | null = null;
  private baseFovDeg: number | null = null;

  constructor(scene: THREE.Scene, container: HTMLElement) {
    this.scene = scene;
    this.container = container;
    this.css3dScene = new THREE.Scene();

    // Setup CSS3D renderer
    this.css3dRenderer = new CSS3DRenderer();

    // Set initial size (will be updated when container is available)
    if (container.clientWidth > 0 && container.clientHeight > 0) {
      this.css3dRenderer.setSize(container.clientWidth, container.clientHeight);
      this.css3dRenderer.domElement.style.position = "absolute";
      this.css3dRenderer.domElement.style.top = "0";
      this.css3dRenderer.domElement.style.left = "0";
      this.css3dRenderer.domElement.style.pointerEvents = "none";
      // Keep hotspots behind skin components (skin overlay uses z-index: 2000).
      this.css3dRenderer.domElement.style.zIndex = String(HOTSPOT_LAYER_Z_INDEX);
      this.configureCSS3DLayeringMode();

      // Add CSS3D renderer to container
      container.appendChild(this.css3dRenderer.domElement);
    } else {
      // Container not ready yet - will be initialized later
      console.log("[HotspotManager] Container not ready, CSS3D renderer will be initialized later");
    }
  }

  setPreviewMode(isPreviewMode: boolean) {
    this.isPreviewMode = isPreviewMode;

    Object.values(this.htmlHotspots).forEach((hotspot) => {
      const element = hotspot.element as HTMLElement | undefined;
      if (!element) return;

      element.classList.toggle("hotspot-preview-mode", isPreviewMode);
      element.classList.toggle("hotspot-editor-mode", !isPreviewMode);
      if (!element.classList.contains("hotspot-is-transforming")) {
        element.style.transition = isPreviewMode ? "all 0.3s ease" : "none";
      }
      this.applyCheckboxInteractivity(element);
    });

    this.refreshHotspotStacking();
  }

  setCurrentRoute(route: "editor" | "skin" | "cms" | "settings" | "trash") {
    this.currentRoute = route;
  }

  private applyCheckboxInteractivity(element: HTMLElement) {
    for (const input of element.querySelectorAll("input[type='checkbox']")) {
      (input as HTMLInputElement).disabled = !this.isPreviewMode;
    }
  }

  private applyTextAspectCompensation(
    element: HTMLElement,
    contentType: "text" | "icon",
    scale: { x: number; y: number },
  ) {
    const textInner = element.querySelector(".hotspot-text-inner") as HTMLElement | null;
    if (!textInner) return;

    if (contentType !== "text") {
      textInner.style.transform = "none";
      return;
    }

    const safeScaleX = Math.max(0.05, scale.x);
    const safeScaleY = Math.max(0.05, scale.y);
    const uniformScale = Math.min(safeScaleX, safeScaleY);
    const scaleX = uniformScale / safeScaleX;
    const scaleY = uniformScale / safeScaleY;
    textInner.style.transform = `scale(${scaleX}, ${scaleY})`;
  }

  setRenderStyle(style: "billboard" | "3d") {
    this.renderStyle = style;
  }

  // Helper method to validate hotspot data
  private validateHotspotData(hotspot: Hotspot): boolean {
    try {
      // Check if hotspot has required fields
      if (!hotspot || !hotspot.id) {
        console.warn("Hotspot missing required ID:", hotspot);
        return false;
      }

      // Validate content if it exists
      if (hotspot.content) {
        const content = getHotspotContent(hotspot);

        // If it's an SVG hotspot, validate the SVG content
        if (hotspot.type === "svg" && content.svg) {
          const svgString = content.svg;
          if (typeof svgString !== "string" || svgString.trim() === "") {
            console.warn("Invalid SVG content for hotspot:", hotspot.id);
            return false;
          }

          // Basic SVG validation
          if (!svgString.includes("<svg")) {
            console.warn("SVG content does not contain <svg> tag for hotspot:", hotspot.id);
            return false;
          }
        }

        // Validate color if present
        if (content.color && typeof content.color !== "string") {
          console.warn("Invalid color type for hotspot:", hotspot.id);
          return false;
        }
      }

      // Validate position
      if (hotspot.position) {
        const pos = getHotspotPosition(hotspot);
        if (typeof pos !== "object" || pos === null) {
          console.warn("Invalid position object for hotspot:", hotspot.id);
          return false;
        }

        // Check for NaN values
        if (Number.isNaN(pos.x) || Number.isNaN(pos.y) || Number.isNaN(pos.z)) {
          console.warn("Position contains NaN values for hotspot:", hotspot.id);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error("Error validating hotspot data:", hotspot?.id, error);
      return false;
    }
  }

  async processHotspots(hotspots: Hotspot[]) {
    const processVersion = ++this.currentProcessVersion;
    const visibleHotspots = hotspots.filter((hotspot) => hotspot.visible !== false);
    const oldStackById = new Map(this.hotspotStackById);
    this.hotspotStackById = new Map(
      visibleHotspots.map((hotspot, index) => [hotspot.id, visibleHotspots.length - index]),
    );

    const processedIds = new Set<string>();
    const operations: Array<() => Promise<void>> = [];

    for (const hotspot of hotspots) {
      try {
        // Validate hotspot data before processing
        if (!this.validateHotspotData(hotspot)) {
          console.warn("Skipping invalid hotspot:", hotspot?.id);
          continue;
        }

        processedIds.add(hotspot.id);

        const existingMesh = this.hotspotMeshes[hotspot.id];
        const existingHTML = this.htmlHotspots[hotspot.id];
        const existingObject =
          hotspot.type === "html" ? existingHTML || existingMesh : existingMesh || existingHTML;
        const visible = hotspot.visible !== false; // default true

        // Debug: Log visibility state
        if (hotspot.id === "test-hotspot-id") {
          // Replace with actual hotspot ID for debugging
          console.log("🔍 Hotspot visibility check:", {
            hotspotId: hotspot.id,
            visible: hotspot.visible,
            computedVisible: visible,
            existingMesh: !!existingMesh || !!existingHTML,
          });
        }

        // If not visible, ensure object removed
        if (!visible) {
          if (existingObject) {
            console.log("🔍 Removing hidden hotspot:", hotspot.id);
            this.removeHotspot(hotspot.id);
            this.removeHTMLHotspot(hotspot.id);
          }
          continue;
        }

        if (!existingObject) {
          // No object yet – add new
          operations.push(() => this.addHotspot(hotspot, processVersion));
          continue;
        }

        // Object exists – decide if we need to recreate or just update in place
        const positionChanged = this.hasPositionChanged(
          hotspot.position,
          existingObject.userData.lastPosition,
        );
        const contentChanged = this.hasContentChanged(
          hotspot.content,
          existingObject.userData.lastContent,
        );
        const scaleChanged = this.hasScaleChanged(hotspot.scale, existingObject.userData.lastScale);
        const rotationChanged = this.hasRotationChanged(
          hotspot.rotation,
          existingObject.userData.lastRotation,
        );
        const typeChanged = hotspot.type !== existingObject.userData.lastType;

        console.log("[Hotspot] process", {
          id: hotspot.id,
          type: hotspot.type,
          positionChanged,
          contentChanged,
          scaleChanged,
          rotationChanged,
          typeChanged,
        });

        if (typeChanged) {
          // Re-create object when type changes
          this.removeHotspot(hotspot.id);
          this.removeHTMLHotspot(hotspot.id);
          operations.push(() => this.addHotspot(hotspot, processVersion));
          continue;
        }

        if (contentChanged) {
          if (hotspot.type === "html") {
            console.log("[Hotspot] updating html content", {
              id: hotspot.id,
              width: (hotspot.content as HotspotContent | undefined)?.width,
              height: (hotspot.content as HotspotContent | undefined)?.height,
              fontSize: (hotspot.content as HotspotContent | undefined)?.fontSize,
              text: (hotspot.content as HotspotContent | undefined)?.text,
            });
            this.updateHTMLHotspotContent(hotspot);
          } else if (hotspot.type === "video") {
            this.updateVideoHotspotContent(hotspot);
          } else {
            // Re-create mesh when sprite content changes
            this.removeHotspot(hotspot.id);
            operations.push(() => this.addHotspot(hotspot, processVersion));
            continue;
          }
        }

        const priorityChanged =
          oldStackById.get(hotspot.id) !== this.getHotspotStackPriority(hotspot.id);
        if (positionChanged || priorityChanged) {
          this.updateHotspotPosition(hotspot);
        }

        if (scaleChanged) {
          this.updateHotspotScale(hotspot);
        }

        if (rotationChanged && (hotspot.type === "html" || hotspot.type === "video")) {
          this.updateHotspotRotation(hotspot);
        }

        this.applyHotspotStacking(hotspot.id);

        existingObject.userData.lastPosition = hotspot.position;
        existingObject.userData.lastContent = hotspot.content;
        existingObject.userData.lastScale = hotspot.scale;
        existingObject.userData.lastType = hotspot.type;
        existingObject.userData.lastRotation = hotspot.rotation;
      } catch (error) {
        console.error("Error processing hotspot:", hotspot?.id, error);
        // Continue processing other hotspots
      }
    }

    // Remove meshes that are no longer present in hotspots array
    Object.keys(this.hotspotMeshes).forEach((id) => {
      if (!processedIds.has(id)) this.removeHotspot(id);
    });

    // Remove HTML hotspots that are no longer present in hotspots array
    Object.keys(this.htmlHotspots).forEach((id) => {
      if (!processedIds.has(id)) this.removeHTMLHotspot(id);
    });

    // Execute all operations in sequence
    for (const operation of operations) {
      try {
        await operation();
        // Check if this process version is still current
        if (processVersion !== this.currentProcessVersion) {
          break; // Abort if a newer process has started
        }
      } catch (error) {
        console.error("Error executing hotspot operation:", error);
        // Continue with other operations
      }
    }

    this.refreshHotspotStacking();
  }

  private getHotspotStackPriority(hotspotId: string): number {
    return this.hotspotStackById.get(hotspotId) ?? 0;
  }

  private applyHotspotStacking(hotspotId: string) {
    const stackPriority = this.getHotspotStackPriority(hotspotId);

    const mesh = this.hotspotMeshes[hotspotId];
    if (mesh) {
      mesh.renderOrder = (this.isPreviewMode ? 1200 : 1000) + stackPriority;
      if (mesh instanceof THREE.Sprite) {
        mesh.material.depthWrite = false;
        mesh.material.depthTest = false;
        mesh.material.needsUpdate = true;
      }
    }

    const htmlHotspot = this.htmlHotspots[hotspotId];
    if (htmlHotspot) {
      htmlHotspot.renderOrder = (this.isPreviewMode ? 2200 : 2000) + stackPriority;
      const element = htmlHotspot.element as HTMLElement | undefined;
      if (element) {
        element.style.zIndex = `${1000 + stackPriority}`;
      }
    }
  }

  private refreshHotspotStacking() {
    const ids = new Set([...Object.keys(this.hotspotMeshes), ...Object.keys(this.htmlHotspots)]);
    ids.forEach((id) => {
      this.applyHotspotStacking(id);
    });
    this.refreshHTMLSceneOrder();
  }

  private refreshHTMLSceneOrder() {
    const orderedHtmlHotspots = Object.entries(this.htmlHotspots)
      .sort((a, b) => this.getHotspotStackPriority(a[0]) - this.getHotspotStackPriority(b[0]))
      .map(([, hotspot]) => hotspot);

    for (const hotspot of orderedHtmlHotspots) {
      if (hotspot.parent === this.css3dScene) {
        this.css3dScene.remove(hotspot);
      }
      this.css3dScene.add(hotspot);
    }
  }

  private applyLayerOffsetToPosition(position: THREE.Vector3, hotspotId: string): THREE.Vector3 {
    const stackPriority = this.getHotspotStackPriority(hotspotId);
    // Keep the bottom layer at its authored radius and pull higher layers closer to camera.
    const layerOffset = Math.min(40, Math.max(0, stackPriority - 1) * 6);
    if (layerOffset <= 0) return position;

    const radius = position.length();
    if (radius <= 0.0001) return position;

    const targetRadius = Math.max(50, radius - layerOffset);
    return position.clone().setLength(targetRadius);
  }

  private updateHotspotPosition(hotspot: Hotspot) {
    const existingObject = this.hotspotMeshes[hotspot.id] || this.htmlHotspots[hotspot.id];
    if (!existingObject) return;

    // Skip position updates for the hotspot being dragged
    if (this.isDragging && this.draggedHotspotId === hotspot.id) {
      return;
    }

    try {
      const position = getHotspotPosition(hotspot);
      let x = position?.x || 0;
      let y = position?.y || 0;
      let z = position?.z || -400;

      // Validate position values
      if (Number.isNaN(x) || Number.isNaN(y) || Number.isNaN(z)) {
        console.warn("Invalid position values for hotspot:", hotspot.id, { x, y, z });
        return;
      }

      // Preserve the original hotspot coordinates without normalizing
      // Only apply scaling if the hotspot is outside the acceptable range
      const distance = Math.sqrt(x * x + y * y + z * z);
      const minRadius = 300;
      const maxRadius = 400;

      if (distance > 0 && (distance < minRadius || distance > maxRadius)) {
        // Only normalize and scale if the distance is outside the acceptable range
        const direction = new THREE.Vector3(x, y, z).normalize();
        const targetRadius = 400;
        const newPosition = direction.multiplyScalar(targetRadius);
        x = newPosition.x;
        y = newPosition.y;
        z = newPosition.z;
      }

      const layeredPosition = this.applyLayerOffsetToPosition(
        new THREE.Vector3(x, y, z),
        hotspot.id,
      );
      existingObject.position.copy(layeredPosition);
      existingObject.userData.lastPosition = hotspot.position;
      if (hotspot.type === "html") {
        this.applyHTMLHotspotPixelCompensation(hotspot, existingObject as CSS3DObject);
      }
    } catch (error) {
      console.error("Error updating hotspot position:", hotspot.id, error);
    }
  }

  private hasPositionChanged(newPosition: HotspotPosition, lastPosition: HotspotPosition): boolean {
    if (!newPosition && !lastPosition) return false;
    if (!newPosition || !lastPosition) return true;

    try {
      return (
        Math.abs((newPosition.x || 0) - (lastPosition.x || 0)) > 0.01 ||
        Math.abs((newPosition.y || 0) - (lastPosition.y || 0)) > 0.01 ||
        Math.abs((newPosition.z || 0) - (lastPosition.z || 0)) > 0.01
      );
    } catch (error) {
      console.error("Error comparing positions:", error);
      return true; // Assume changed if we can't compare
    }
  }

  private hasScaleChanged(newScale: HotspotScale, lastScale: HotspotScale): boolean {
    if (!newScale && !lastScale) return false;
    if (!newScale || !lastScale) return true;

    try {
      return (
        Math.abs((newScale.x || 1) - (lastScale.x || 1)) > 0.001 ||
        Math.abs((newScale.y || 1) - (lastScale.y || 1)) > 0.001 ||
        Math.abs((newScale.z || 1) - (lastScale.z || 1)) > 0.001
      );
    } catch (error) {
      console.error("Error comparing scales:", error);
      return true;
    }
  }

  private hasRotationChanged(newRotation: any, lastRotation: any): boolean {
    if (!newRotation && !lastRotation) return false;
    if (!newRotation || !lastRotation) return true;

    try {
      return (
        Math.abs((newRotation.x || 0) - (lastRotation.x || 0)) > 0.1 ||
        Math.abs((newRotation.y || 0) - (lastRotation.y || 0)) > 0.1 ||
        Math.abs((newRotation.z || 0) - (lastRotation.z || 0)) > 0.1
      );
    } catch (error) {
      console.error("Error comparing rotations:", error);
      return true;
    }
  }

  private updateHotspotRotation(hotspot: Hotspot) {
    const renderStyle = getHotspotRenderStyle(hotspot, this.renderStyle);
    const htmlHotspot = this.htmlHotspots[hotspot.id];
    if (htmlHotspot && renderStyle === "3d" && hotspot.rotation) {
      const rot = hotspot.rotation as any;
      // Create the base facing quaternion (towards center)
      const facingQuat = new THREE.Quaternion();
      htmlHotspot.lookAt(0, 0, 0);
      facingQuat.copy(htmlHotspot.quaternion);

      // Create rotation quaternion from Euler angles
      const euler = new THREE.Euler(
        (rot.x || 0) * (Math.PI / 180),
        (rot.y || 0) * (Math.PI / 180),
        (rot.z || 0) * (Math.PI / 180),
        "XYZ"
      );
      const rotationQuat = new THREE.Quaternion().setFromEuler(euler);

      // Combine: first face center, then apply rotation
      htmlHotspot.quaternion.multiplyQuaternions(facingQuat, rotationQuat);
      htmlHotspot.userData.lastRotation = hotspot.rotation;
    }

    const videoMesh = this.hotspotMeshes[hotspot.id];
    if (videoMesh && videoMesh.userData?.isVideo && renderStyle === "3d" && hotspot.rotation) {
      const rot = hotspot.rotation as any;
      const facingQuat = new THREE.Quaternion();
      const tempObject = new THREE.Object3D();
      tempObject.position.copy(videoMesh.position);
      tempObject.lookAt(0, 0, 0);
      facingQuat.copy(tempObject.quaternion);

      const euler = new THREE.Euler(
        (rot.x || 0) * (Math.PI / 180),
        (rot.y || 0) * (Math.PI / 180),
        (rot.z || 0) * (Math.PI / 180),
        "XYZ"
      );
      const rotationQuat = new THREE.Quaternion().setFromEuler(euler);
      videoMesh.quaternion.multiplyQuaternions(facingQuat, rotationQuat);
      videoMesh.userData.lastRotation = hotspot.rotation;
    }
  }

  private updateHotspotScale(hotspot: Hotspot) {
    this.applyHotspotScale(hotspot.id, getHotspotScale(hotspot));
  }

  private applyHotspotScale(hotspotId: string, scaleValue: HotspotScale) {
    const normalizedScale = {
      x: Number.isFinite(scaleValue?.x) ? (scaleValue?.x as number) : 1,
      y: Number.isFinite(scaleValue?.y) ? (scaleValue?.y as number) : 1,
      z: Number.isFinite(scaleValue?.z) ? (scaleValue?.z as number) : 1,
    };

    const clampedScale = {
      x: Math.max(0.05, normalizedScale.x),
      y: Math.max(0.05, normalizedScale.y),
      z: Math.max(0.05, normalizedScale.z),
    };

    const mesh = this.hotspotMeshes[hotspotId];
    if (mesh) {
      const baseScaleX = mesh.userData.baseScaleX || mesh.scale.x || 1;
      const baseScaleY = mesh.userData.baseScaleY || mesh.scale.y || 1;
      const baseScaleZ = mesh.userData.baseScaleZ || mesh.scale.z || 1;

      mesh.userData.baseScaleX = baseScaleX;
      mesh.userData.baseScaleY = baseScaleY;
      mesh.userData.baseScaleZ = baseScaleZ;
      mesh.scale.set(
        baseScaleX * clampedScale.x,
        baseScaleY * clampedScale.y,
        baseScaleZ * clampedScale.z,
      );
      mesh.userData.lastScale = { ...clampedScale };
    }

    const htmlHotspot = this.htmlHotspots[hotspotId];
    if (htmlHotspot) {
      // For CSS3D elements, apply Z scale as a visual effect on the DOM element
      // Z scale affects opacity and size to create a depth illusion
      const element = htmlHotspot.element as HTMLElement | undefined;
      if (element) {
        // Apply Z scale: closer (higher Z) = larger, farther = smaller
        const zScaleFactor = clampedScale.z;

        // Apply a filter effect: higher Z = sharper (more blur at lower Z)
        const blurAmount = Math.max(0, (1 - zScaleFactor) * 2);
        element.style.filter = blurAmount > 0 ? `blur(${blurAmount}px)` : "none";
      }

      htmlHotspot.scale.set(clampedScale.x, clampedScale.y, clampedScale.z);
      htmlHotspot.userData.lastScale = { ...clampedScale };
      if (element) {
        const contentType = element.dataset.contentType === "icon" ? "icon" : "text";
        this.applyTextAspectCompensation(element, contentType, clampedScale);
      }
    }
  }

  private hasContentChanged(newContent: HotspotContent, lastContent: HotspotContent): boolean {
    try {
      return JSON.stringify(newContent || {}) !== JSON.stringify(lastContent || {});
    } catch (error) {
      console.error("Error comparing content:", error);
      return true; // Assume changed if we can't compare
    }
  }

  private createVideoElement(settings: ReturnType<typeof getVideoSettings>) {
    const video = document.createElement("video");
    const url = settings.videoUrl || "";
    if (url.startsWith("http://") || url.startsWith("https://")) {
      video.crossOrigin = "anonymous";
    }
    video.preload = "auto";
    video.loop = settings.videoLoop;
    video.muted = settings.videoMuted;
    video.autoplay = settings.videoAutoplay;
    video.playsInline = true;
    video.setAttribute("playsinline", "true");
    const setAndPlay = () => {
      try {
        video.load();
        if (settings.videoAutoplay) {
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch(() => {});
          }
        }
      } catch (_error) {
        // Autoplay might be blocked; ignore.
      }
    };

    if (url.startsWith("app://storage/")) {
      void fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to fetch local video: ${response.status}`);
          }
          return response.blob();
        })
        .then((blob) => {
          const objectUrl = URL.createObjectURL(blob);
          video.dataset.objectUrl = objectUrl;
          video.src = objectUrl;
          setAndPlay();
        })
        .catch(() => {
          // Fall back to direct URL if blob loading fails.
          video.src = url;
          setAndPlay();
        });
    } else {
      video.src = url;
      setAndPlay();
    }
    return video;
  }

  private createVideoHotspot(hotspot: Hotspot): THREE.Mesh {
    const content = getHotspotContent(hotspot) || {};
    const settings = getVideoSettings(content);

    let texture: THREE.Texture;
    let videoElement: HTMLVideoElement | undefined;
    if (settings.videoUrl) {
      videoElement = this.createVideoElement(settings);
      const videoTexture = new THREE.VideoTexture(videoElement);
      videoTexture.generateMipmaps = false;
      videoTexture.minFilter = THREE.LinearFilter;
      videoTexture.magFilter = THREE.LinearFilter;
      videoTexture.colorSpace = THREE.SRGBColorSpace;
      videoTexture.wrapS = THREE.ClampToEdgeWrapping;
      videoTexture.wrapT = THREE.ClampToEdgeWrapping;
      texture = videoTexture;
    } else {
      texture = createVideoPlaceholderTexture();
    }

    const material = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: texture },
        keyColor: { value: new THREE.Color(settings.chromaKeyColor) },
        similarity: { value: settings.chromaKeySimilarity },
        smoothness: { value: settings.chromaKeySmoothness },
        spill: { value: settings.chromaKeySpill },
        useChroma: { value: settings.chromaKeyEnabled ? 1 : 0 },
      },
      vertexShader: VIDEO_VERTEX_SHADER,
      fragmentShader: VIDEO_FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
    });

    const geometry = new THREE.PlaneGeometry(1, 1);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.scale.set(settings.videoWidth, settings.videoHeight, 1);
    mesh.renderOrder = this.isPreviewMode ? 1100 : 1000;
    mesh.userData = {
      isVideo: true,
      videoElement,
      videoTexture: texture,
      videoMaterial: material,
      videoUrl: settings.videoUrl,
      baseScaleX: settings.videoWidth,
      baseScaleY: settings.videoHeight,
      baseScaleZ: 1,
      lastRotation: hotspot.rotation,
      renderStyle: getHotspotRenderStyle(hotspot, this.renderStyle),
    };
    return mesh;
  }

  private updateVideoHotspotContent(hotspot: Hotspot) {
    const mesh = this.hotspotMeshes[hotspot.id];
    if (!mesh || !mesh.userData?.isVideo) return;
    const content = getHotspotContent(hotspot) || {};
    const settings = getVideoSettings(content);
    mesh.userData.renderStyle = getHotspotRenderStyle(hotspot, this.renderStyle);

    const material = mesh.userData.videoMaterial as THREE.ShaderMaterial | undefined;
    if (material) {
      material.uniforms.keyColor.value.set(settings.chromaKeyColor);
      material.uniforms.similarity.value = settings.chromaKeySimilarity;
      material.uniforms.smoothness.value = settings.chromaKeySmoothness;
      material.uniforms.spill.value = settings.chromaKeySpill;
      material.uniforms.useChroma.value = settings.chromaKeyEnabled ? 1 : 0;
    }

    if (mesh.userData.videoUrl !== settings.videoUrl) {
      const existingVideo = mesh.userData.videoElement as HTMLVideoElement | undefined;
      if (existingVideo) {
        try {
          existingVideo.pause();
          existingVideo.removeAttribute("src");
          existingVideo.load();
          const objectUrl = existingVideo.dataset?.objectUrl;
          if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
          }
        } catch (_error) {
          // Ignore cleanup errors.
        }
      }

      const existingTexture = mesh.userData.videoTexture as THREE.Texture | undefined;
      if (existingTexture) {
        existingTexture.dispose();
      }

      let nextTexture: THREE.Texture;
      let nextVideoElement: HTMLVideoElement | undefined;
      if (settings.videoUrl) {
        nextVideoElement = this.createVideoElement(settings);
        const videoTexture = new THREE.VideoTexture(nextVideoElement);
        videoTexture.generateMipmaps = false;
        videoTexture.minFilter = THREE.LinearFilter;
        videoTexture.magFilter = THREE.LinearFilter;
        videoTexture.colorSpace = THREE.SRGBColorSpace;
        videoTexture.wrapS = THREE.ClampToEdgeWrapping;
        videoTexture.wrapT = THREE.ClampToEdgeWrapping;
        nextTexture = videoTexture;
      } else {
        nextTexture = createVideoPlaceholderTexture();
      }

      if (material) {
        material.uniforms.map.value = nextTexture;
      }

      mesh.userData.videoElement = nextVideoElement;
      mesh.userData.videoTexture = nextTexture;
      mesh.userData.videoUrl = settings.videoUrl;
    } else {
      const existingVideo = mesh.userData.videoElement as HTMLVideoElement | undefined;
      if (existingVideo) {
        existingVideo.loop = settings.videoLoop;
        existingVideo.muted = settings.videoMuted;
        existingVideo.autoplay = settings.videoAutoplay;
        if (settings.videoAutoplay) {
          const playPromise = existingVideo.play();
          if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch(() => {});
          }
        }
      }
    }

    const baseScaleX = mesh.userData.baseScaleX as number | undefined;
    const baseScaleY = mesh.userData.baseScaleY as number | undefined;
    if (
      Number.isFinite(settings.videoWidth) &&
      Number.isFinite(settings.videoHeight) &&
      (baseScaleX !== settings.videoWidth || baseScaleY !== settings.videoHeight)
    ) {
      mesh.userData.baseScaleX = settings.videoWidth;
      mesh.userData.baseScaleY = settings.videoHeight;
      mesh.userData.baseScaleZ = 1;
      this.applyHotspotScale(hotspot.id, getHotspotScale(hotspot));
    }
  }

  private async addHotspot(hotspot: Hotspot, processVersion: number = this.currentProcessVersion) {
    // Handle HTML hotspots with CSS3D
    if (hotspot.type === "html") {
      this.addHTMLHotspot(hotspot);
      return;
    }
    // Skip if a newer process has started while awaiting async operations
    const isStale = () => processVersion !== this.currentProcessVersion;

    let hotspotMesh: THREE.Mesh | THREE.Sprite;

    try {
      if (hotspot.type === "video") {
        hotspotMesh = this.createVideoHotspot(hotspot);
      } else if (hotspot.type === "svg" && hotspot.content && getHotspotContent(hotspot).svg) {
        try {
          const svgString = getHotspotContent(hotspot).svg;

          // Additional validation for SVG string
          if (!svgString || typeof svgString !== "string" || svgString.trim() === "") {
            console.warn("Invalid SVG string for hotspot:", hotspot.id);
            hotspotMesh = this.createIconHotspot(hotspot);
          } else {
            const texture = await createSVGTexture(svgString);
            if (isStale()) return; // Abort if stale
            const material = new THREE.SpriteMaterial({
              map: texture,
              transparent: true,
              alphaTest: 0.1,
              depthWrite: false,
              depthTest: false,
              blending: THREE.NormalBlending,
            });
            hotspotMesh = new THREE.Sprite(material);
            // Use slightly larger scale in preview mode to improve visibility
            const scale = this.isPreviewMode ? 45 : 40;
            hotspotMesh.scale.set(scale, scale, 1);
            // Higher render order in preview mode to ensure visibility
            hotspotMesh.renderOrder = this.isPreviewMode ? 1100 : 1000;
          }
        } catch (error) {
          console.error("Error creating SVG texture for hotspot:", hotspot.id, error);
          hotspotMesh = this.createIconHotspot(hotspot);
        }
      } else {
        hotspotMesh = this.createIconHotspot(hotspot);
      }

      if (isStale()) return; // Abort if stale after potentially async texture generation

      const position = getHotspotPosition(hotspot);

      // Position hotspots closer to camera to avoid clipping with sphere
      let x = (position?.x as number) || 0;
      let y = (position?.y as number) || 0;
      let z = (position?.z as number) || -400;

      // Validate position values
      if (Number.isNaN(x) || Number.isNaN(y) || Number.isNaN(z)) {
        console.warn("Invalid position values for hotspot:", hotspot.id, { x, y, z });
        x = 0;
        y = 0;
        z = -400; // Use default position
      }

      // Preserve the original hotspot coordinates without normalizing
      // This ensures hotspots stay in their exact positions when switching panoramas

      // Only apply the target radius scaling if the hotspot is too far from the center
      // This prevents hotspots from being too close to the camera or too far away
      const distance = Math.sqrt(x * x + y * y + z * z);
      const minRadius = 300;
      const maxRadius = 400;

      if (distance > 0 && (distance < minRadius || distance > maxRadius)) {
        // Only normalize and scale if the distance is outside the acceptable range
        const direction = new THREE.Vector3(x, y, z).normalize();
        const targetRadius = 400;
        const newPosition = direction.multiplyScalar(targetRadius);
        x = newPosition.x;
        y = newPosition.y;
        z = newPosition.z;
      }

      const layeredPosition = this.applyLayerOffsetToPosition(
        new THREE.Vector3(x, y, z),
        hotspot.id,
      );
      hotspotMesh.position.copy(layeredPosition);
      const existingUserData = hotspotMesh.userData || {};
      const baseScaleX = existingUserData.baseScaleX || hotspotMesh.scale.x || 1;
      const baseScaleY = existingUserData.baseScaleY || hotspotMesh.scale.y || 1;
      const baseScaleZ = existingUserData.baseScaleZ || hotspotMesh.scale.z || 1;
      hotspotMesh.userData = {
        ...existingUserData,
        id: hotspot.id,
        baseScaleX,
        baseScaleY,
        baseScaleZ,
        lastPosition: hotspot.position,
        lastContent: hotspot.content,
        lastScale: hotspot.scale,
        lastType: hotspot.type,
        lastRotation: hotspot.rotation,
      };
      this.scene.add(hotspotMesh);
      this.hotspotMeshes[hotspot.id] = hotspotMesh;
      this.applyHotspotScale(hotspot.id, getHotspotScale(hotspot));
      this.applyHotspotStacking(hotspot.id);
    } catch (error) {
      console.error("Error adding hotspot:", hotspot.id, error);
      // Create a fallback hotspot if something goes wrong
      try {
        hotspotMesh = this.createIconHotspot(hotspot);
        const fallbackRadius = 400;
        hotspotMesh.position.copy(
          this.applyLayerOffsetToPosition(new THREE.Vector3(0, 0, -fallbackRadius), hotspot.id),
        );
        const existingUserData = hotspotMesh.userData || {};
        const baseScaleX = existingUserData.baseScaleX || hotspotMesh.scale.x || 1;
        const baseScaleY = existingUserData.baseScaleY || hotspotMesh.scale.y || 1;
        const baseScaleZ = existingUserData.baseScaleZ || hotspotMesh.scale.z || 1;
        hotspotMesh.userData = {
          ...existingUserData,
          id: hotspot.id,
          baseScaleX,
          baseScaleY,
          baseScaleZ,
          lastPosition: hotspot.position,
          lastContent: hotspot.content,
          lastScale: hotspot.scale,
          lastType: hotspot.type,
          lastRotation: hotspot.rotation,
        };
        this.scene.add(hotspotMesh);
        this.hotspotMeshes[hotspot.id] = hotspotMesh;
        this.applyHotspotScale(hotspot.id, getHotspotScale(hotspot));
        this.applyHotspotStacking(hotspot.id);
      } catch (fallbackError) {
        console.error("Error creating fallback hotspot:", hotspot.id, fallbackError);
      }
    }
  }

  private createIconHotspot(hotspot: Hotspot): THREE.Sprite {
    try {
      const content = getHotspotContent(hotspot) || {};
      const color = resolveCSSVariable(
        content.color && typeof content.color === "string" ? content.color : "#2196f3",
      );
      const iconType = content.iconType || hotspot.type || "pin";

      const texture = createHotspotIconTexture(color, iconType);
      const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        alphaTest: 0.1,
        depthWrite: false,
        depthTest: false,
        blending: THREE.NormalBlending,
      });
      const sprite = new THREE.Sprite(material);
      // Use slightly larger scale in preview mode to improve visibility
      const scale = this.isPreviewMode ? 45 : 40;
      sprite.scale.set(scale, scale, 1); // Consistent size for full circle
      // Higher render order in preview mode to ensure visibility
      sprite.renderOrder = this.isPreviewMode ? 1100 : 1000;
      return sprite;
    } catch (error) {
      console.error("Error creating icon texture for hotspot:", hotspot.id, error);
      // Final fallback to simple circle
      try {
        const circleGeometry = new THREE.CircleGeometry(12, 32);
        const circleMaterial = new THREE.MeshBasicMaterial({
          color: new THREE.Color("#2196f3"),
          side: THREE.DoubleSide,
          transparent: true,
          opacity: 0.8,
        });
        const _mesh = new THREE.Mesh(circleGeometry, circleMaterial);

        // Convert mesh to sprite for consistency
        const canvas = document.createElement("canvas");
        canvas.width = 64;
        canvas.height = 64;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = "#2196f3";
          ctx.beginPath();
          ctx.arc(32, 32, 28, 0, 2 * Math.PI);
          ctx.fill();
        }
        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
          alphaTest: 0.1,
          depthWrite: false,
          depthTest: false,
          blending: THREE.NormalBlending,
        });
        const sprite = new THREE.Sprite(material);
        sprite.renderOrder = 1000; // Render after the panorama sphere
        return sprite;
      } catch (finalError) {
        console.error("Error creating final fallback hotspot:", hotspot.id, finalError);
        // Return a very basic sprite as last resort with anti-flickering settings
        const texture = new THREE.CanvasTexture(document.createElement("canvas"));
        const material = new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
          alphaTest: 0.1,
          depthWrite: false,
          depthTest: false,
          blending: THREE.NormalBlending,
        });
        const sprite = new THREE.Sprite(material);
        sprite.renderOrder = 1000; // Render after the panorama sphere
        return sprite;
      }
    }
  }

  private removeHotspot(id: string) {
    const mesh = this.hotspotMeshes[id];
    if (mesh) {
      try {
        this.scene.remove(mesh);
        if (mesh.userData?.isVideo) {
          const videoElement = mesh.userData.videoElement as HTMLVideoElement | undefined;
          if (videoElement) {
            try {
              videoElement.pause();
              videoElement.removeAttribute("src");
              videoElement.load();
              const objectUrl = videoElement.dataset?.objectUrl;
              if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
              }
            } catch (_error) {
              // Ignore cleanup errors.
            }
          }
          const videoTexture = mesh.userData.videoTexture as THREE.Texture | undefined;
          if (videoTexture) {
            videoTexture.dispose();
          }
        }
        if (mesh instanceof THREE.Mesh) {
          mesh.geometry.dispose();
          const mat = mesh.material;
          if (Array.isArray(mat)) {
            mat.forEach((m) => {
              m.dispose();
            });
          } else if (mat) {
            mat.dispose();
          }
        } else if (mesh instanceof THREE.Sprite) {
          const mat = mesh.material;
          if (mat) {
            mat.dispose();
          }
        }
        delete this.hotspotMeshes[id];
        if (!this.htmlHotspots[id]) {
          this.hotspotStackById.delete(id);
        }
      } catch (error) {
        console.error("Error removing hotspot:", id, error);
        // Still remove from tracking even if disposal fails
        delete this.hotspotMeshes[id];
        if (!this.htmlHotspots[id]) {
          this.hotspotStackById.delete(id);
        }
      }
    }
  }

  /**
   * Smoothly animates all hotspot meshes to the given scale (x, y, z) over the specified duration (ms).
   */
  animateHotspotScale(targetScale: number, duration: number = 50) {
    const meshes = Object.values(this.hotspotMeshes);
    const startScales = meshes.map((mesh) => mesh.scale.clone());
    const endScale = new THREE.Vector3(targetScale, targetScale, 1);
    const startTime = performance.now();
    const animate = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      for (let i = 0; i < meshes.length; i++) {
        const mesh = meshes[i];
        const start = startScales[i];
        mesh.scale.lerpVectors(start, endScale, t);
      }
      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        for (const mesh of meshes) mesh.scale.copy(endScale);
      }
    };
    requestAnimationFrame(animate);
  }

  private addHTMLHotspot(hotspot: Hotspot) {
    // Remove existing HTML hotspot if it exists
    this.removeHTMLHotspot(hotspot.id);

    // Create HTML element
    const element = document.createElement("div");
    element.className = `html-hotspot ${this.isPreviewMode ? "hotspot-preview-mode" : "hotspot-editor-mode"}`;
    element.dataset.hotspotId = hotspot.id;
    const { project, panoramas } = useEditorStore.getState();
    const content = getHotspotContent(hotspot);
    const contentType = content?.contentType === "icon" ? "icon" : "text";
    const iconContent = resolveIconContentValue(content, project?.craftspace_id, project?.id);
    const rawDisplayText = contentType === "icon" ? iconContent : ((content?.text as string) || "");
    const targetPanoramaName = resolveLinkedPanoramaName(content, panoramas);
    const currentPanoramaName = resolveCurrentPanoramaName(hotspot, panoramas);
    const displayText =
      contentType === "icon"
        ? rawDisplayText
        : resolveTextVariables(rawDisplayText, {
            hotspot,
            project,
            targetPanoramaName,
            currentPanoramaName,
          });
    const iconSvgMarkup = contentType === "icon" && isSvgMarkup(iconContent) ? iconContent : "";
    const iconImageSrc = contentType === "icon" && !iconSvgMarkup && isImageSource(iconContent) ? iconContent : "";
    const subtitleContent =
      contentType === "text" && typeof content?.subtitle === "string"
        ? resolveTextVariables(content.subtitle, {
            hotspot,
            project,
            targetPanoramaName,
            currentPanoramaName,
          })
        : content?.subtitle;
    const hasVisibleText =
      (typeof displayText === "string" && displayText.trim() !== "") ||
      (typeof subtitleContent === "string" && subtitleContent.trim() !== "");
    const borderWidthValue = resolveSizeValue(content?.borderWidth, "0px");
    const normalizedOpacity = getNormalizedOpacity(content?.opacity, 1);
    const effectiveOpacity = normalizedOpacity;
    const effectiveTransform =
      this.isPreviewMode &&
      typeof content?.transform === "string" &&
      content.transform.trim() !== ""
        ? content.transform
        : "none";
    element.dataset.contentType = contentType;

    const outlineWidthValue = resolveSizeValue(content?.outlineWidth, "0px");
    element.style.cssText = `
      opacity: ${effectiveOpacity};
      transform: ${effectiveTransform};
      transition: ${this.isPreviewMode ? "all 0.3s ease" : "none"};
      pointer-events: auto;
      cursor: pointer;
      position: relative;
      isolation: isolate;
      z-index: 0;
      min-width: 20px;
      min-height: 20px;
      overflow: visible;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      image-rendering: auto;
      backface-visibility: hidden;
      -webkit-backface-visibility: hidden;
    `;

    const contentWrapper = document.createElement("div");
    contentWrapper.className = "html-hotspot-content";
    contentWrapper.style.cssText = `
      background-color: ${resolveCSSVariable(content?.backgroundColor || "#000000CC")};
      border-radius: ${resolveSizeValue(content?.borderRadius, "4px")};
      padding: ${resolveHotspotPadding(content)};
      color: ${resolveCSSVariable(content?.textColor || content?.color || "#ffffff")};
      border: ${borderWidthValue} ${content?.borderStyle || "solid"} ${resolveCSSVariable(content?.borderColor || "transparent")};
      outline: ${outlineWidthValue} ${content?.outlineStyle || "solid"} ${resolveCSSVariable(content?.outlineColor || "transparent")};
      outline-offset: ${resolveSizeValue(content?.outlineOffset, "0px")};
      font-size: ${resolveSizeValue(content?.fontSize, "14px")};
      font-weight: ${content?.fontWeight || "500"};
      font-family: ${content?.fontFamily || "inherit"};
      text-align: ${content?.textAlign || "center"};
      line-height: ${content?.lineHeight || "1.4"};
      letter-spacing: ${content?.letterSpacing || "0px"};
      text-shadow: ${hasVisibleText ? content?.textShadow || "none" : "none"};
      box-shadow: ${hasVisibleText ? content?.boxShadow || "none" : "none"};
      text-decoration: ${content?.textDecoration || "none"};
      text-transform: ${content?.textTransform || "none"};
      font-style: ${content?.fontStyle || "normal"};
      white-space: normal;
      aspect-ratio: ${
        content?.aspectRatio === "custom" && content?.customAspectRatio
          ? content.customAspectRatio
          : content?.aspectRatio || "auto"
      };
      width: ${content?.width || "auto"};
      height: ${content?.height || "auto"};
      min-width: 20px;
      min-height: 20px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      position: relative;
      overflow: hidden;
      z-index: 1;
    `;

    if (contentType === "icon") {
      const widthValue = typeof content?.width === "string" ? content.width.trim() : "";
      const heightValue = typeof content?.height === "string" ? content.height.trim() : "";
      const explicitWidth = widthValue !== "" && widthValue !== "auto";
      const explicitHeight = heightValue !== "" && heightValue !== "auto";
      if (!explicitWidth && !explicitHeight) {
        const fontSizeValue = resolveNumberValue(content?.fontSize, 24);
        const sizePx = Math.max(20, Math.round(fontSizeValue || 24));
        const fallbackSize = `${sizePx}px`;
        contentWrapper.style.width = fallbackSize;
        contentWrapper.style.height = fallbackSize;
        contentWrapper.style.padding = "0px";
      }
    }

    element.appendChild(contentWrapper);

    const contentWrapperWidthPx = parsePixelValue(contentWrapper.style.width);
    const contentWrapperHeightPx = parsePixelValue(contentWrapper.style.height);
    const iconSizeValue = resolveIconSizeValue(
      content?.iconSize ?? "100%",
      `${Math.min(contentWrapperWidthPx ?? 48, contentWrapperHeightPx ?? 48)}px`,
      contentWrapperWidthPx,
      contentWrapperHeightPx,
    );

    // Add content
    if (iconSvgMarkup) {
      const safeSvg = sanitizeSvgMarkup(iconSvgMarkup);
      if (safeSvg) {
        const iconWrapper = document.createElement("div");
        iconWrapper.className = "hotspot-content";
        iconWrapper.style.position = "relative";
        iconWrapper.style.zIndex = "1";
        iconWrapper.style.width = iconSizeValue;
        iconWrapper.style.height = iconSizeValue;
        iconWrapper.style.display = "flex";
        iconWrapper.style.alignItems = "center";
        iconWrapper.style.justifyContent = "center";
        iconWrapper.style.margin = "0 auto";
        iconWrapper.innerHTML = safeSvg;
        const svgEl = iconWrapper.querySelector("svg");
        if (svgEl) {
          svgEl.setAttribute("width", "100%");
          svgEl.setAttribute("height", "100%");
          svgEl.style.maxWidth = "100%";
          svgEl.style.maxHeight = "100%";
          svgEl.style.display = "block";
        }
        contentWrapper.appendChild(iconWrapper);
      }
    } else if (iconImageSrc) {
      const iconWrapper = document.createElement("div");
      iconWrapper.className = "hotspot-content";
      iconWrapper.style.position = "relative";
      iconWrapper.style.zIndex = "1";
      iconWrapper.style.width = iconSizeValue;
      iconWrapper.style.height = iconSizeValue;
      iconWrapper.style.display = "flex";
      iconWrapper.style.alignItems = "center";
      iconWrapper.style.justifyContent = "center";
      iconWrapper.style.margin = "0 auto";
      const img = document.createElement("img");
      img.src = iconImageSrc;
      img.alt = "";
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.objectFit = "contain";
      iconWrapper.appendChild(img);
      contentWrapper.appendChild(iconWrapper);
    } else if (displayText) {
      if (contentType === "icon") {
        contentWrapper.style.fontSize = iconSizeValue;
      }
      const textContainer = document.createElement("div");
      textContainer.className = "hotspot-text-content";
      textContainer.style.transformOrigin = "center center";
      textContainer.style.position = "relative";
      textContainer.style.width = "100%";
      textContainer.style.height = "auto";
      textContainer.style.minHeight = "1em";
      textContainer.style.flex = "0 0 auto";
      textContainer.style.display = "flex";
      textContainer.style.flexDirection = "column";
      textContainer.style.alignItems = "center";
      textContainer.style.justifyContent = "center";
      textContainer.style.textAlign = "center";
      textContainer.style.zIndex = "1";
      const textInner = document.createElement("div");
      textInner.className = "hotspot-text-inner";
      textInner.style.transformOrigin = "center center";
      textInner.style.position = "relative";
      textInner.style.width = "100%";
      textInner.style.minHeight = "1em";
      textInner.style.whiteSpace = "pre-wrap";
      textInner.style.wordBreak = "break-word";
      textInner.style.overflowWrap = "break-word";
      textInner.innerHTML = sanitizeHotspotHTML(displayText);
      textContainer.appendChild(textInner);
      contentWrapper.appendChild(textContainer);
      for (const p of contentWrapper.querySelectorAll("p")) {
        (p as HTMLElement).style.margin = "0";
        (p as HTMLElement).style.padding = "0";
        (p as HTMLElement).style.minHeight = "0";
      }
      // Strip native list styling — markers are injected as text spans for reliable CSS3D rendering
      for (const list of contentWrapper.querySelectorAll("ul, ol")) {
        const el = list as HTMLElement;
        el.style.listStyle = "none";
        el.style.margin = "0";
        el.style.padding = "0";
        el.style.width = "100%";
        el.style.textAlign = "left";
      }
      // Bullet lists: inject "•" span before each item's content
      for (const ul of contentWrapper.querySelectorAll("ul:not([data-type='taskList'])")) {
        for (const li of (ul as HTMLElement).querySelectorAll(":scope > li")) {
          const liEl = li as HTMLElement;
          liEl.style.cssText += "margin:0;padding:0;display:flex;align-items:flex-start;";
          const marker = document.createElement("span");
          marker.textContent = "•";
          marker.style.cssText = "flex-shrink:0;margin-right:0.4em;user-select:none;";
          liEl.insertBefore(marker, liEl.firstChild);
        }
      }
      // Ordered lists: inject "1." "2." etc.
      for (const ol of contentWrapper.querySelectorAll("ol")) {
        let idx = 0;
        for (const li of (ol as HTMLElement).querySelectorAll(":scope > li")) {
          idx++;
          const liEl = li as HTMLElement;
          liEl.style.cssText += "margin:0;padding:0;display:flex;align-items:flex-start;";
          const marker = document.createElement("span");
          marker.textContent = `${idx}.`;
          marker.style.cssText = "flex-shrink:0;margin-right:0.4em;user-select:none;";
          liEl.insertBefore(marker, liEl.firstChild);
        }
      }
      // Task list items: label (checkbox) + div (text) side by side
      for (const li of contentWrapper.querySelectorAll("li[data-type='taskItem']")) {
        const liEl = li as HTMLElement;
        liEl.style.cssText += "margin:0;padding:0;display:flex;align-items:flex-start;gap:4px;";
      }
      for (const label of contentWrapper.querySelectorAll("li[data-type='taskItem'] > label")) {
        (label as HTMLElement).style.flexShrink = "0";
        (label as HTMLElement).style.marginTop = "1px";
      }
      for (const div of contentWrapper.querySelectorAll("li[data-type='taskItem'] > div")) {
        (div as HTMLElement).style.flex = "1";
      }
      this.applyCheckboxInteractivity(contentWrapper);
    }

    // Add subtitle if present
    if (contentType === "text" && subtitleContent) {
      const subtitle = document.createElement("div");
      subtitle.style.cssText = `
        font-size: calc(14px - 2px);
        opacity: 0.8;
        margin-top: 4px;
      `;
      subtitle.innerHTML = sanitizeHotspotHTML(subtitleContent as string);
      const textContainer = contentWrapper.querySelector(".hotspot-text-content");
      if (textContainer) {
        textContainer.appendChild(subtitle);
      } else {
        element.appendChild(subtitle);
      }
    }

    const { selectedPanorama } = useEditorStore.getState();
    const currentPanoramaId = selectedPanorama?.id;
    const navigateTargets = getNavigateTargetPanoramaIds(content);
    const isActive =
      Boolean(currentPanoramaId) &&
      navigateTargets.some((targetId) => targetId === currentPanoramaId);
    const visitedIds = getVisitedPanoramaIds(panoramas);
    const isVisited =
      !isActive && navigateTargets.some((targetId) => visitedIds.has(targetId));
    const activeStyle = isActive ? this.getActiveStyle(content, hasVisibleText) : null;
    const visitedStyle = isVisited ? this.getVisitedStyle(content, hasVisibleText) : null;
    if (activeStyle) {
      this.applyHoverStyle(element, activeStyle);
    } else if (visitedStyle) {
      this.applyHoverStyle(element, visitedStyle);
    }
    if (isActive && content.activeTransform !== undefined) {
      const transformValue =
        typeof content.activeTransform === "string" && content.activeTransform.trim() !== ""
          ? content.activeTransform
          : "none";
      element.style.transform = transformValue as string;
    } else if (isVisited && content.visitedTransform !== undefined) {
      const transformValue =
        typeof content.visitedTransform === "string" && content.visitedTransform.trim() !== ""
          ? content.visitedTransform
          : "none";
      element.style.transform = transformValue as string;
    }

    this.configureHoverStyles(element, content, hasVisibleText, activeStyle || visitedStyle);
    this.configureHotspotPointer(element, content, getHotspotScale(hotspot));
    this.configureTooltip(element, content, hotspot);

    // Keep data-checked in sync when a task list checkbox is toggled in preview mode
    element.addEventListener("change", (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.type !== "checkbox") return;
      const li = target.closest("li[data-type='taskItem']");
      if (li) li.setAttribute("data-checked", String(target.checked));
    });

    // Add click event listener for hotspot actions
    element.addEventListener("click", (e: MouseEvent) => {
      // In preview mode, let Viewer360's unified click path handle actions so
      // preview/edit navigation behavior stays consistent.
      if (this.isPreviewMode) {
        // Checkbox clicks are handled by the change listener above
        if ((e.target as HTMLElement).tagName === "INPUT") {
          e.stopPropagation();
        }
        return;
      }

      e.stopPropagation();

      // Lock hotspots in skin editor when not in preview mode
      if (this.currentRoute === "skin" && !this.isPreviewMode) return;

      // Checkbox clicks are handled by the change listener above — don't trigger hotspot actions
      if ((e.target as HTMLElement).tagName === "INPUT") return;

      // Check if we should trigger actions based on mode and modifier key
      const isModifierPressed = e.metaKey || e.ctrlKey; // Command key on Mac, Ctrl on Windows/Linux
      const shouldTriggerActions = this.isPreviewMode || isModifierPressed;
      import("@/store/editorStore").then(({ useEditorStore }) => {
        const store = useEditorStore.getState();
        const latestHotspot = store.hotspots.find((item) => item.id === hotspot.id) || hotspot;

        if (shouldTriggerActions) {
          this.executeHotspotActions(latestHotspot);
        } else {
          this.selectHotspot(latestHotspot);
        }
      });
    });

    // Create CSS3D object
    const css3dObject = new CSS3DObject(element);

    // Position on sphere (same logic as SVG hotspots)
    const position = getHotspotPosition(hotspot);
    let x = (position?.x as number) || 0;
    let y = (position?.y as number) || 0;
    let z = (position?.z as number) || -400;

    // Validate position values
    if (Number.isNaN(x) || Number.isNaN(y) || Number.isNaN(z)) {
      console.warn("Invalid position values for HTML hotspot:", hotspot.id, { x, y, z });
      x = 0;
      y = 0;
      z = -400; // Use default position
    }

    // Preserve the original hotspot coordinates without normalizing
    // Only apply scaling if the hotspot is outside the acceptable range
    const distance = Math.sqrt(x * x + y * y + z * z);
    const minRadius = 300;
    const maxRadius = 400;

    let finalPosition: THREE.Vector3;
    if (distance > 0 && (distance < minRadius || distance > maxRadius)) {
      // Only normalize and scale if the distance is outside the acceptable range
      const direction = new THREE.Vector3(x, y, z).normalize();
      const targetRadius = 400;
      finalPosition = direction.multiplyScalar(targetRadius);
    } else {
      // Use the original coordinates
      finalPosition = new THREE.Vector3(x, y, z);
    }
    css3dObject.position.copy(this.applyLayerOffsetToPosition(finalPosition, hotspot.id));

    // Store references
    css3dObject.userData = {
      id: hotspot.id,
      type: "html",
      lastPosition: hotspot.position,
      lastContent: hotspot.content,
      lastScale: hotspot.scale,
      lastType: hotspot.type,
      lastRotation: hotspot.rotation,
      renderStyle: getHotspotRenderStyle(hotspot, this.renderStyle),
    };

    // Set up initial orientation to face the center of the sphere
    css3dObject.lookAt(0, 0, 0);

    // Apply rotation if in 3D mode
    if (getHotspotRenderStyle(hotspot, this.renderStyle) === "3d" && hotspot.rotation) {
      const rot = hotspot.rotation as any;
      // Create the base facing quaternion (towards center)
      const facingQuat = new THREE.Quaternion();
      facingQuat.copy(css3dObject.quaternion);

      // Create rotation quaternion from Euler angles
      const euler = new THREE.Euler(
        (rot.x || 0) * (Math.PI / 180),
        (rot.y || 0) * (Math.PI / 180),
        (rot.z || 0) * (Math.PI / 180),
        "XYZ"
      );
      const rotationQuat = new THREE.Quaternion().setFromEuler(euler);

      // Combine: first face center, then apply rotation
      css3dObject.quaternion.multiplyQuaternions(facingQuat, rotationQuat);
    }

    this.css3dScene.add(css3dObject);
    this.htmlHotspots[hotspot.id] = css3dObject;
    this.applyHTMLHotspotPixelCompensation(hotspot, css3dObject);
    this.applyHotspotScale(hotspot.id, getHotspotScale(hotspot));
    this.applyHotspotStacking(hotspot.id);

    // Force one deferred refresh so large hotspots don't stay rasterized at low quality on initial load.
    requestAnimationFrame(() => {
      if (!this.htmlHotspots[hotspot.id]) return;
      this.updateHTMLHotspotContent(hotspot);
      this.applyHotspotScale(hotspot.id, getHotspotScale(hotspot));
      this.applyHotspotStacking(hotspot.id);
      this.requestSharpnessRerender(3);
      setTimeout(() => this.requestSharpnessRerender(2), 80);
    });
  }

  private removeHTMLHotspot(hotspotId: string) {
    const existingHTMLHotspot = this.htmlHotspots[hotspotId];
    if (existingHTMLHotspot) {
      const element = existingHTMLHotspot.element as HTMLElement | undefined;
      if (element) {
        this.teardownHoverHandlers(element);
      }
      this.css3dScene.remove(existingHTMLHotspot);
      delete this.htmlHotspots[hotspotId];
      if (!this.hotspotMeshes[hotspotId]) {
        this.hotspotStackById.delete(hotspotId);
      }
    }
  }

  private resolveEffectiveOpacity(value: unknown) {
    const normalized = getNormalizedOpacity(value, 1);
    return this.isPreviewMode ? normalized : Math.max(0.35, normalized);
  }

  private getBaseHoverStyle(
    content: HotspotContent,
    hasVisibleText: boolean,
    activeStyle?: HotspotHoverStyle | null,
  ): HotspotHoverStyle {
    const baseStyle: HotspotHoverStyle = {
      backgroundColor: resolveCSSVariable(content.backgroundColor || "#000000CC"),
      borderColor: resolveCSSVariable(content.borderColor || "transparent"),
      color: resolveCSSVariable(content.textColor || content.color || "#ffffff"),
      boxShadow: hasVisibleText ? (content.boxShadow as string) || "none" : "none",
      textShadow: hasVisibleText ? (content.textShadow as string) || "0 1px 2px rgba(0, 0, 0, 0.5)" : "none",
      opacity: String(this.resolveEffectiveOpacity(content.opacity)),
    };
    if (!activeStyle) return baseStyle;
    return { ...baseStyle, ...activeStyle };
  }

  private getHoverStyle(content: HotspotContent, hasVisibleText: boolean): HotspotHoverStyle | null {
    const hoverStyle: HotspotHoverStyle = {};

    if (content.hoverBackgroundColor !== undefined) {
      hoverStyle.backgroundColor = resolveCSSVariable(content.hoverBackgroundColor || "transparent");
    }
    if (content.hoverBorderColor !== undefined) {
      hoverStyle.borderColor = resolveCSSVariable(content.hoverBorderColor || "transparent");
    }
    if (content.hoverTextColor !== undefined) {
      hoverStyle.color = resolveCSSVariable(content.hoverTextColor || "#ffffff");
    }
    if (content.hoverBoxShadow !== undefined) {
      hoverStyle.boxShadow = hasVisibleText ? (content.hoverBoxShadow as string) || "none" : "none";
    }
    if (content.hoverTextShadow !== undefined) {
      hoverStyle.textShadow = hasVisibleText ? (content.hoverTextShadow as string) || "none" : "none";
    }
    if (content.hoverOpacity !== undefined) {
      hoverStyle.opacity = String(this.resolveEffectiveOpacity(content.hoverOpacity));
    }
    return Object.keys(hoverStyle).length > 0 ? hoverStyle : null;
  }

  private getActiveStyle(content: HotspotContent, hasVisibleText: boolean): HotspotHoverStyle | null {
    const activeStyle: HotspotHoverStyle = {};

    if (content.activeBackgroundColor !== undefined) {
      activeStyle.backgroundColor = resolveCSSVariable(content.activeBackgroundColor || "transparent");
    }
    if (content.activeBorderColor !== undefined) {
      activeStyle.borderColor = resolveCSSVariable(content.activeBorderColor || "transparent");
    }
    if (content.activeTextColor !== undefined) {
      activeStyle.color = resolveCSSVariable(content.activeTextColor || "#ffffff");
    }
    if (content.activeBoxShadow !== undefined) {
      activeStyle.boxShadow = hasVisibleText ? (content.activeBoxShadow as string) || "none" : "none";
    }
    if (content.activeTextShadow !== undefined) {
      activeStyle.textShadow = hasVisibleText ? (content.activeTextShadow as string) || "none" : "none";
    }
    if (content.activeOpacity !== undefined) {
      activeStyle.opacity = String(this.resolveEffectiveOpacity(content.activeOpacity));
    }
    return Object.keys(activeStyle).length > 0 ? activeStyle : null;
  }

  private getVisitedStyle(content: HotspotContent, hasVisibleText: boolean): HotspotHoverStyle | null {
    const visitedStyle: HotspotHoverStyle = {};

    if (content.visitedBackgroundColor !== undefined) {
      visitedStyle.backgroundColor = resolveCSSVariable(content.visitedBackgroundColor || "transparent");
    }
    if (content.visitedBorderColor !== undefined) {
      visitedStyle.borderColor = resolveCSSVariable(content.visitedBorderColor || "transparent");
    }
    if (content.visitedTextColor !== undefined) {
      visitedStyle.color = resolveCSSVariable(content.visitedTextColor || "#ffffff");
    }
    if (content.visitedBoxShadow !== undefined) {
      visitedStyle.boxShadow = hasVisibleText ? (content.visitedBoxShadow as string) || "none" : "none";
    }
    if (content.visitedTextShadow !== undefined) {
      visitedStyle.textShadow = hasVisibleText ? (content.visitedTextShadow as string) || "none" : "none";
    }
    if (content.visitedOpacity !== undefined) {
      visitedStyle.opacity = String(this.resolveEffectiveOpacity(content.visitedOpacity));
    }
    return Object.keys(visitedStyle).length > 0 ? visitedStyle : null;
  }

  private applyHoverStyle(element: HTMLElement, style: HotspotHoverStyle) {
    const contentWrapper =
      (element.querySelector(".html-hotspot-content") as HTMLElement | null) || element;
    if (style.backgroundColor !== undefined) {
      contentWrapper.style.backgroundColor = style.backgroundColor;
    }
    if (style.borderColor !== undefined) {
      contentWrapper.style.borderColor = style.borderColor;
    }
    if (style.color !== undefined) {
      contentWrapper.style.color = style.color;
    }
    if (style.boxShadow !== undefined) {
      contentWrapper.style.boxShadow = style.boxShadow;
    }
    if (style.textShadow !== undefined) {
      contentWrapper.style.textShadow = style.textShadow;
    }
    if (style.opacity !== undefined) {
      element.style.opacity = style.opacity;
    }
  }

  private configureHoverStyles(
    element: HTMLElement,
    content: HotspotContent,
    hasVisibleText: boolean,
    activeStyle?: HotspotHoverStyle | null,
  ) {
    const hoverStyles = this.getHoverStyle(content, hasVisibleText);
    const hoverElement = element as HotspotHoverElement;
    const isHovered = Boolean(hoverElement.__hotspotIsHovered);
    const contentWrapper =
      (element.querySelector(".html-hotspot-content") as HTMLElement | null) || element;

    hoverElement.__hotspotBaseStyle = isHovered
      ? this.getBaseHoverStyle(content, hasVisibleText, activeStyle)
      : {
          backgroundColor: contentWrapper.style.backgroundColor,
          borderColor: contentWrapper.style.borderColor,
          color: contentWrapper.style.color,
          boxShadow: contentWrapper.style.boxShadow,
          textShadow: contentWrapper.style.textShadow,
          opacity: element.style.opacity,
        };

    hoverElement.__hotspotHoverStyle = hoverStyles;

    if (!hoverStyles) {
      this.teardownHoverHandlers(element);
      return;
    }

    if (!hoverElement.__hotspotHoverHandler || !hoverElement.__hotspotUnhoverHandler) {
      hoverElement.__hotspotHoverHandler = () => {
        hoverElement.__hotspotIsHovered = true;
        if (hoverElement.__hotspotHoverStyle) {
          this.applyHoverStyle(hoverElement, hoverElement.__hotspotHoverStyle);
        }
      };
      hoverElement.__hotspotUnhoverHandler = () => {
        hoverElement.__hotspotIsHovered = false;
        if (hoverElement.__hotspotBaseStyle) {
          this.applyHoverStyle(hoverElement, hoverElement.__hotspotBaseStyle);
        }
      };
      element.addEventListener("mouseenter", hoverElement.__hotspotHoverHandler);
      element.addEventListener("mouseleave", hoverElement.__hotspotUnhoverHandler);
    }

    if (isHovered && hoverStyles) {
      this.applyHoverStyle(element, hoverStyles);
    }
  }

  private teardownHoverHandlers(element: HTMLElement) {
    const hoverElement = element as HotspotHoverElement;
    if (hoverElement.__hotspotHoverHandler) {
      element.removeEventListener("mouseenter", hoverElement.__hotspotHoverHandler);
    }
    if (hoverElement.__hotspotUnhoverHandler) {
      element.removeEventListener("mouseleave", hoverElement.__hotspotUnhoverHandler);
    }
    hoverElement.__hotspotHoverHandler = undefined;
    hoverElement.__hotspotUnhoverHandler = undefined;
    hoverElement.__hotspotHoverStyle = null;
    hoverElement.__hotspotIsHovered = false;
  }

  private configureHotspotPointer(
    element: HTMLElement,
    content: HotspotContent,
    scaleValue?: HotspotScale,
  ) {
    const existingPointer = element.querySelector(".html-hotspot-pointer");
    if (existingPointer) existingPointer.remove();
    const existingLine = element.querySelector(".html-hotspot-pointer-line");
    if (existingLine) existingLine.remove();

    if (!content.showPointer) return;

    const pointer = document.createElement("div");
    pointer.className = "html-hotspot-pointer";
    const bgColor = resolveCSSVariable(content?.backgroundColor || "#000000CC");
    const direction = resolveArrowDirection(content.pointerDirection, "bottom");
    const size = resolveNumberValue(content.pointerSize, 8);
    const overlap = resolveNumberValue(content.pointerOverlap, 2);
    const cornerRadius = resolveNumberValue(content.borderRadius, 0);
    const lineThickness = resolveNumberValue(content.pointerLineThickness, 2);
    const scaleX =
      scaleValue && Number.isFinite(scaleValue.x as number) ? (scaleValue.x as number) : 1;
    const scaleY =
      scaleValue && Number.isFinite(scaleValue.y as number) ? (scaleValue.y as number) : 1;
    const inverseScale = {
      x: scaleX !== 0 ? 1 / scaleX : 1,
      y: scaleY !== 0 ? 1 / scaleY : 1,
    };
    const lineGap = Math.max(0, -(overlap + 0.5));
    const pointerShape = (content.pointerShape as string) || "triangle";

    if (pointerShape === "circle") {
      const diameter = size * 2;
      const effectiveOverlap = overlap + 0.5;
      if (lineGap > 0) {
        const line = document.createElement("div");
        line.className = "html-hotspot-pointer-line";
        applyHotspotPointerLineStyles(line, "bottom", bgColor, lineGap, lineThickness);
        element.appendChild(line);
      }
      // Pre-distort dimensions so that the subsequent scale(ix, iy) restores a true circle
      pointer.style.position = "absolute";
      pointer.style.width = `${diameter * scaleX}px`;
      pointer.style.height = `${diameter * scaleY}px`;
      pointer.style.borderRadius = "50%";
      pointer.style.backgroundColor = bgColor;
      pointer.style.zIndex = "-1";
      pointer.style.top = `calc(100% - ${effectiveOverlap}px)`;
      pointer.style.left = "50%";
      pointer.style.transformOrigin = "center center";
      pointer.style.transform = `translate(-50%, -50%) scale(${inverseScale.x}, ${inverseScale.y})`;
      element.appendChild(pointer);
      return;
    }

    if (lineGap > 0) {
      const line = document.createElement("div");
      line.className = "html-hotspot-pointer-line";
      applyHotspotPointerLineStyles(line, direction, bgColor, lineGap, lineThickness);
      element.appendChild(line);
    }

    applyHotspotPointerStyles(pointer, direction, bgColor, size, overlap, cornerRadius, inverseScale);
    element.appendChild(pointer);
  }

  private configureTooltip(element: HTMLElement, content: HotspotContent, hotspot: Hotspot) {
    const hoverElement = element as HotspotHoverElement;

    // Remove old tooltip handlers
    if (hoverElement.__hotspotTooltipHoverHandler) {
      element.removeEventListener("mouseenter", hoverElement.__hotspotTooltipHoverHandler);
    }
    if (hoverElement.__hotspotTooltipUnhoverHandler) {
      element.removeEventListener("mouseleave", hoverElement.__hotspotTooltipUnhoverHandler);
    }
    hoverElement.__hotspotTooltipHoverHandler = undefined;
    hoverElement.__hotspotTooltipUnhoverHandler = undefined;

    // Remove existing tooltip element
    const existingTooltip = element.querySelector(".html-hotspot-tooltip");
    if (existingTooltip) existingTooltip.remove();

    if (!content.tooltipEnabled) return;

    const { project, panoramas } = useEditorStore.getState();
    const targetPanoramaName = resolveLinkedPanoramaName(content, panoramas);
    const currentPanoramaName = resolveCurrentPanoramaName(hotspot, panoramas);
    const tooltipText = resolveTextVariables((content.tooltipText as string) || "", {
      hotspot,
      project,
      targetPanoramaName,
      currentPanoramaName,
    });
    const tooltip = document.createElement("div");
    tooltip.className = "html-hotspot-tooltip";
    tooltip.textContent = tooltipText;
    tooltip.style.display = element.dataset.tooltipPreview === "true" ? "block" : "none";
    tooltip.style.backgroundColor = resolveCSSVariable((content.tooltipBgColor as string) || "#000000CC");
    tooltip.style.color = resolveCSSVariable((content.tooltipFontColor as string) || "#ffffff");
    tooltip.style.fontSize = (content.tooltipFontSize as string) || "12px";
    tooltip.style.fontWeight = (content.tooltipFontWeight as string) || "400";
    tooltip.style.borderRadius = (content.tooltipBorderRadius as string) || "4px";
    tooltip.style.padding = (content.tooltipPadding as string) || "4px 8px";
    const tooltipArrowDirection = resolveArrowDirection(content.tooltipArrowDirection, "top");
    applyTooltipPlacement(tooltip, tooltipArrowDirection, 8);

    if (content.tooltipArrow) {
      const bgColor = resolveCSSVariable((content.tooltipBgColor as string) || "#000000CC");
      const arrow = document.createElement("div");
      arrow.className = "html-hotspot-tooltip-arrow";
      applyTooltipArrowStyles(arrow, tooltipArrowDirection, bgColor, 6);
      tooltip.appendChild(arrow);
    }

    element.appendChild(tooltip);

    hoverElement.__hotspotTooltipHoverHandler = () => {
      tooltip.style.display = "block";
    };
    hoverElement.__hotspotTooltipUnhoverHandler = () => {
      tooltip.style.display = "none";
    };
    element.addEventListener("mouseenter", hoverElement.__hotspotTooltipHoverHandler);
    element.addEventListener("mouseleave", hoverElement.__hotspotTooltipUnhoverHandler);
  }

  private updateHTMLHotspotContent(hotspot: Hotspot) {
    const existingHTMLHotspot = this.htmlHotspots[hotspot.id];
    if (!existingHTMLHotspot || !existingHTMLHotspot.element) {
      // If no existing HTML hotspot, create a new one
      this.addHTMLHotspot(hotspot);
      return;
    }

    const { project, panoramas, selectedPanorama } = useEditorStore.getState();
    const content = getHotspotContent(hotspot) || {};
    const contentType = content.contentType === "icon" ? "icon" : "text";
    const iconContent = resolveIconContentValue(content, project?.craftspace_id, project?.id);
    const rawDisplayText = contentType === "icon" ? iconContent : ((content.text as string) || "");
    const targetPanoramaName = resolveLinkedPanoramaName(content, panoramas);
    const currentPanoramaName = resolveCurrentPanoramaName(hotspot, panoramas);
    const displayText =
      contentType === "icon"
        ? rawDisplayText
        : resolveTextVariables(rawDisplayText, {
            hotspot,
            project,
            targetPanoramaName,
            currentPanoramaName,
          });
    const iconSvgMarkup = contentType === "icon" && isSvgMarkup(iconContent) ? iconContent : "";
    const iconImageSrc = contentType === "icon" && !iconSvgMarkup && isImageSource(iconContent) ? iconContent : "";
    const resolvedSubtitle =
      contentType === "text" && typeof content.subtitle === "string"
        ? resolveTextVariables(content.subtitle, {
            hotspot,
            project,
            targetPanoramaName,
            currentPanoramaName,
          })
        : content.subtitle;
    const hasVisibleText =
      (typeof displayText === "string" && displayText.trim() !== "") ||
      (typeof resolvedSubtitle === "string" && resolvedSubtitle.trim() !== "");
    const currentPanoramaId = selectedPanorama?.id;
    const navigateTargets = getNavigateTargetPanoramaIds(content);
    const isActive =
      Boolean(currentPanoramaId) &&
      navigateTargets.some((targetId) => targetId === currentPanoramaId);
    const visitedIds = getVisitedPanoramaIds(panoramas);
    const isVisited =
      !isActive && navigateTargets.some((targetId) => visitedIds.has(targetId));
    const activeStyle = isActive ? this.getActiveStyle(content, hasVisibleText) : null;
    const visitedStyle = isVisited ? this.getVisitedStyle(content, hasVisibleText) : null;
    const debugHotspotText =
      contentType === "text" &&
      typeof displayText === "string" &&
      displayText.trim() !== "" &&
      (content?.width !== undefined || content?.height !== undefined);
    const element = existingHTMLHotspot.element;
    element.dataset.contentType = contentType;
    element.classList.toggle("hotspot-preview-mode", this.isPreviewMode);
    element.classList.toggle("hotspot-editor-mode", !this.isPreviewMode);
    if (!element.classList.contains("hotspot-is-transforming")) {
      element.style.transition = this.isPreviewMode ? "all 0.3s ease" : "none";
    }
    element.style.isolation = "isolate";
    element.style.zIndex = "0";

    // Update text content
    const textContent = displayText;
    const subtitleContent = resolvedSubtitle || "";

    const contentWrapper = document.createElement("div");
    contentWrapper.className = "html-hotspot-content";
    contentWrapper.style.cssText = `
      background-color: ${resolveCSSVariable(content?.backgroundColor || "#000000CC")};
      border-radius: ${resolveSizeValue(content?.borderRadius, "4px")};
      padding: ${resolveHotspotPadding(content)};
      color: ${resolveCSSVariable(content?.textColor || content?.color || "#ffffff")};
      border: ${resolveSizeValue(content?.borderWidth, "0px")} ${content?.borderStyle || "solid"} ${resolveCSSVariable(content?.borderColor || "transparent")};
      outline: ${resolveSizeValue(content?.outlineWidth, "0px")} ${content?.outlineStyle || "solid"} ${resolveCSSVariable(content?.outlineColor || "transparent")};
      outline-offset: ${resolveSizeValue(content?.outlineOffset, "0px")};
      font-size: ${resolveSizeValue(content?.fontSize, "14px")};
      font-weight: ${content?.fontWeight || "500"};
      font-family: ${content?.fontFamily || "inherit"};
      text-align: ${content?.textAlign || "center"};
      line-height: ${content?.lineHeight || "1.4"};
      letter-spacing: ${content?.letterSpacing || "0px"};
      text-shadow: ${hasVisibleText ? content?.textShadow || "none" : "none"};
      box-shadow: ${hasVisibleText ? content?.boxShadow || "none" : "none"};
      text-decoration: ${content?.textDecoration || "none"};
      text-transform: ${content?.textTransform || "none"};
      font-style: ${content?.fontStyle || "normal"};
      white-space: normal;
      aspect-ratio: ${
        content?.aspectRatio === "custom" && content?.customAspectRatio
          ? content.customAspectRatio
          : content?.aspectRatio || "auto"
      };
      width: ${content?.width || "auto"};
      height: ${content?.height || "auto"};
      min-width: 20px;
      min-height: 20px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      position: relative;
      overflow: hidden;
      z-index: 1;
    `;
    if (contentType === "icon") {
      const widthValue = typeof content?.width === "string" ? content.width.trim() : "";
      const heightValue = typeof content?.height === "string" ? content.height.trim() : "";
      const explicitWidth = widthValue !== "" && widthValue !== "auto";
      const explicitHeight = heightValue !== "" && heightValue !== "auto";
      if (!explicitWidth && !explicitHeight) {
        const fontSizeValue = resolveNumberValue(content?.fontSize, 24);
        const sizePx = Math.max(20, Math.round(fontSizeValue || 24));
        const fallbackSize = `${sizePx}px`;
        contentWrapper.style.width = fallbackSize;
        contentWrapper.style.height = fallbackSize;
        contentWrapper.style.padding = "0px";
      }
    }
    const contentWrapperWidthPx = parsePixelValue(contentWrapper.style.width);
    const contentWrapperHeightPx = parsePixelValue(contentWrapper.style.height);
    const iconSizeValue = resolveIconSizeValue(
      content?.iconSize ?? "100%",
      `${Math.min(contentWrapperWidthPx ?? 48, contentWrapperHeightPx ?? 48)}px`,
      contentWrapperWidthPx,
      contentWrapperHeightPx,
    );
    // Add main text
    if (iconSvgMarkup) {
      const safeSvg = sanitizeSvgMarkup(iconSvgMarkup);
      if (safeSvg) {
        const iconWrapper = document.createElement("div");
        iconWrapper.className = "hotspot-content";
        iconWrapper.style.position = "relative";
        iconWrapper.style.zIndex = "1";
        iconWrapper.style.width = iconSizeValue;
        iconWrapper.style.height = iconSizeValue;
        iconWrapper.style.display = "flex";
        iconWrapper.style.alignItems = "center";
        iconWrapper.style.justifyContent = "center";
        iconWrapper.style.margin = "0 auto";
        iconWrapper.innerHTML = safeSvg;
        const svgEl = iconWrapper.querySelector("svg");
        if (svgEl) {
          svgEl.setAttribute("width", "100%");
          svgEl.setAttribute("height", "100%");
          svgEl.style.maxWidth = "100%";
          svgEl.style.maxHeight = "100%";
          svgEl.style.display = "block";
        }
        contentWrapper.appendChild(iconWrapper);
      }
    } else if (iconImageSrc) {
      const iconWrapper = document.createElement("div");
      iconWrapper.className = "hotspot-content";
      iconWrapper.style.position = "relative";
      iconWrapper.style.zIndex = "1";
      iconWrapper.style.width = iconSizeValue;
      iconWrapper.style.height = iconSizeValue;
      iconWrapper.style.display = "flex";
      iconWrapper.style.alignItems = "center";
      iconWrapper.style.justifyContent = "center";
      iconWrapper.style.margin = "0 auto";
      const img = document.createElement("img");
      img.src = iconImageSrc;
      img.alt = "";
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.objectFit = "contain";
      iconWrapper.appendChild(img);
      contentWrapper.appendChild(iconWrapper);
    } else if (textContent) {
      if (contentType === "icon") {
        contentWrapper.style.fontSize = iconSizeValue;
      }
      const textContainer = document.createElement("div");
      textContainer.className = "hotspot-text-content";
      textContainer.style.transformOrigin = "center center";
      textContainer.style.position = "relative";
      textContainer.style.width = "100%";
      textContainer.style.height = "auto";
      textContainer.style.minHeight = "1em";
      textContainer.style.flex = "0 0 auto";
      textContainer.style.display = "flex";
      textContainer.style.flexDirection = "column";
      textContainer.style.alignItems = "center";
      textContainer.style.justifyContent = "center";
      textContainer.style.textAlign = "center";
      textContainer.style.zIndex = "1";
      const textInner = document.createElement("div");
      textInner.className = "hotspot-text-inner";
      textInner.style.transformOrigin = "center center";
      textInner.style.position = "relative";
      textInner.style.width = "100%";
      textInner.style.minHeight = "1em";
      textInner.style.whiteSpace = "pre-wrap";
      textInner.style.wordBreak = "break-word";
      textInner.style.overflowWrap = "break-word";
      textInner.innerHTML = sanitizeHotspotHTML(textContent);
      textContainer.appendChild(textInner);
      contentWrapper.appendChild(textContainer);
      for (const p of contentWrapper.querySelectorAll("p")) {
        (p as HTMLElement).style.margin = "0";
        (p as HTMLElement).style.padding = "0";
        (p as HTMLElement).style.minHeight = "0";
      }
      // Strip native list styling — markers are injected as text spans for reliable CSS3D rendering
      for (const list of contentWrapper.querySelectorAll("ul, ol")) {
        const el = list as HTMLElement;
        el.style.listStyle = "none";
        el.style.margin = "0";
        el.style.padding = "0";
        el.style.width = "100%";
        el.style.textAlign = "left";
      }
      // Bullet lists: inject "•" span before each item's content
      for (const ul of contentWrapper.querySelectorAll("ul:not([data-type='taskList'])")) {
        for (const li of (ul as HTMLElement).querySelectorAll(":scope > li")) {
          const liEl = li as HTMLElement;
          liEl.style.cssText += "margin:0;padding:0;display:flex;align-items:flex-start;";
          const marker = document.createElement("span");
          marker.textContent = "•";
          marker.style.cssText = "flex-shrink:0;margin-right:0.4em;user-select:none;";
          liEl.insertBefore(marker, liEl.firstChild);
        }
      }
      // Ordered lists: inject "1." "2." etc.
      for (const ol of contentWrapper.querySelectorAll("ol")) {
        let idx = 0;
        for (const li of (ol as HTMLElement).querySelectorAll(":scope > li")) {
          idx++;
          const liEl = li as HTMLElement;
          liEl.style.cssText += "margin:0;padding:0;display:flex;align-items:flex-start;";
          const marker = document.createElement("span");
          marker.textContent = `${idx}.`;
          marker.style.cssText = "flex-shrink:0;margin-right:0.4em;user-select:none;";
          liEl.insertBefore(marker, liEl.firstChild);
        }
      }
      // Task list items: label (checkbox) + div (text) side by side
      for (const li of contentWrapper.querySelectorAll("li[data-type='taskItem']")) {
        const liEl = li as HTMLElement;
        liEl.style.cssText += "margin:0;padding:0;display:flex;align-items:flex-start;gap:4px;";
      }
      for (const label of contentWrapper.querySelectorAll("li[data-type='taskItem'] > label")) {
        (label as HTMLElement).style.flexShrink = "0";
        (label as HTMLElement).style.marginTop = "1px";
      }
      for (const div of contentWrapper.querySelectorAll("li[data-type='taskItem'] > div")) {
        (div as HTMLElement).style.flex = "1";
      }
      this.applyCheckboxInteractivity(contentWrapper);
    }

    // Add subtitle if present
    if (contentType === "text" && subtitleContent) {
      const subtitle = document.createElement("div");
      subtitle.style.cssText = `
        font-size: calc(14px - 2px);
        opacity: 0.8;
        margin-top: 4px;
      `;
      subtitle.innerHTML = sanitizeHotspotHTML(subtitleContent);
      const textContainer = contentWrapper.querySelector(".hotspot-text-content");
      if (textContainer) {
        textContainer.appendChild(subtitle);
      } else {
        contentWrapper.appendChild(subtitle);
      }
    }

    // Update styling properties that might have changed
    const visualProperties = [
      "backgroundColor",
      "borderColor",
      "borderStyle",
      "borderWidth",
      "borderRadius",
      "outlineColor",
      "outlineStyle",
      "outlineWidth",
      "outlineOffset",
      "textColor",
      "color",
      "padding",
      "paddingTop",
      "paddingRight",
      "paddingBottom",
      "paddingLeft",
      "fontSize",
      "fontWeight",
      "fontFamily",
      "textAlign",
      "lineHeight",
      "letterSpacing",
      "textShadow",
      "boxShadow",
      "opacity",
      "transform",
      "textDecoration",
      "textTransform",
      "fontStyle",
      "aspectRatio",
      "width",
      "height",
    ];

    for (const prop of visualProperties) {
      if (content[prop] !== undefined) {
        switch (prop) {
          case "backgroundColor":
            contentWrapper.style.backgroundColor = resolveCSSVariable(
              content[prop] || "#000000CC",
            );
            break;
          case "borderColor":
            contentWrapper.style.borderColor = resolveCSSVariable(content[prop] || "#ffffff");
            break;
          case "borderStyle":
            contentWrapper.style.borderStyle = content[prop] || "solid";
            break;
          case "textColor":
          case "color":
            contentWrapper.style.color = resolveCSSVariable(content[prop] || "#ffffff");
            break;
          case "borderWidth":
            contentWrapper.style.borderWidth = resolveSizeValue(content[prop], "0px");
            break;
          case "borderRadius":
            contentWrapper.style.borderRadius = resolveSizeValue(content[prop], "4px");
            break;
          case "outlineColor":
            contentWrapper.style.outlineColor = resolveCSSVariable(content[prop] || "transparent");
            break;
          case "outlineStyle":
            contentWrapper.style.outlineStyle = content[prop] || "solid";
            break;
          case "outlineWidth":
            contentWrapper.style.outlineWidth = resolveSizeValue(content[prop], "0px");
            break;
          case "outlineOffset":
            contentWrapper.style.outlineOffset = resolveSizeValue(content[prop], "0px");
            break;
          case "padding":
          case "paddingTop":
          case "paddingRight":
          case "paddingBottom":
          case "paddingLeft":
            contentWrapper.style.padding = resolveHotspotPadding(content);
            break;
          case "fontSize":
            contentWrapper.style.fontSize = resolveSizeValue(content[prop], "14px");
            break;
          case "fontWeight":
            contentWrapper.style.fontWeight = content[prop] || "500";
            break;
          case "fontFamily":
            contentWrapper.style.fontFamily = content[prop] || "inherit";
            break;
          case "textAlign":
            contentWrapper.style.textAlign = content[prop] || "center";
            break;
          case "lineHeight":
            contentWrapper.style.lineHeight = content[prop] || "1.4";
            break;
          case "letterSpacing":
            contentWrapper.style.letterSpacing = content[prop] || "0px";
            break;
          case "textShadow":
            contentWrapper.style.textShadow = content[prop] || "0 1px 2px rgba(0, 0, 0, 0.5)";
            break;
          case "boxShadow":
            contentWrapper.style.boxShadow = content[prop] || "none";
            break;
          case "opacity":
            element.style.opacity = String(
              this.isPreviewMode
                ? getNormalizedOpacity(content[prop], 1)
                : Math.max(0.35, getNormalizedOpacity(content[prop], 1)),
            );
            break;
          case "transform":
            element.style.transform =
              this.isPreviewMode &&
              typeof content[prop] === "string" &&
              (content[prop] as string).trim() !== ""
                ? (content[prop] as string)
                : "none";
            break;
          case "textDecoration":
            contentWrapper.style.textDecoration = content[prop] || "none";
            break;
          case "textTransform":
            contentWrapper.style.textTransform = content[prop] || "none";
            break;
          case "fontStyle":
            contentWrapper.style.fontStyle = content[prop] || "normal";
            break;
          case "aspectRatio":
            // Handle custom aspect ratio
            if (content[prop] === "custom" && content.customAspectRatio) {
              contentWrapper.style.aspectRatio = content.customAspectRatio;
            } else {
              contentWrapper.style.aspectRatio = content[prop] || "auto";
            }
            break;
          case "width":
            contentWrapper.style.width = content[prop] || "auto";
            break;
          case "height":
            contentWrapper.style.height = content[prop] || "auto";
            break;
        }
      }
    }

    if (!hasVisibleText) {
      contentWrapper.style.boxShadow = "none";
      contentWrapper.style.textShadow = "none";
    }

    if (activeStyle) {
      this.applyHoverStyle(element, activeStyle);
    } else if (visitedStyle) {
      this.applyHoverStyle(element, visitedStyle);
    }
    if (isActive && content.activeTransform !== undefined) {
      const transformValue =
        typeof content.activeTransform === "string" && content.activeTransform.trim() !== ""
          ? content.activeTransform
          : "none";
      element.style.transform = transformValue as string;
    } else if (isVisited && content.visitedTransform !== undefined) {
      const transformValue =
        typeof content.visitedTransform === "string" && content.visitedTransform.trim() !== ""
          ? content.visitedTransform
          : "none";
      element.style.transform = transformValue as string;
    }

    // Keep the outer wrapper stable so text edits don't rebuild the tooltip/pointer subtree.
    const existingContentWrapper = element.querySelector(
      ":scope > .html-hotspot-content",
    ) as HTMLElement | null;
    if (existingContentWrapper) {
      existingContentWrapper.style.cssText = contentWrapper.style.cssText;
      existingContentWrapper.innerHTML = contentWrapper.innerHTML;
      this.applyCheckboxInteractivity(existingContentWrapper);
    } else {
      element.replaceChildren(contentWrapper);
    }

    this.configureHoverStyles(element, content, hasVisibleText, activeStyle || visitedStyle);
    this.configureHotspotPointer(element, content, getHotspotScale(hotspot));
    this.configureTooltip(element, content, hotspot);
    this.applyHTMLHotspotPixelCompensation(hotspot, existingHTMLHotspot);
    if (debugHotspotText) {
      const liveWrapper = element.querySelector(
        ":scope > .html-hotspot-content",
      ) as HTMLElement | null;
      const liveTextInner = element.querySelector(
        ":scope .hotspot-text-inner",
      ) as HTMLElement | null;
      console.log("[Hotspot] text update", {
        id: hotspot.id,
        width: content?.width,
        height: content?.height,
        fontSize: content?.fontSize,
        textLength: displayText.length,
        wrapperSize: liveWrapper
          ? { w: liveWrapper.clientWidth, h: liveWrapper.clientHeight }
          : null,
        textSize: liveTextInner
          ? { w: liveTextInner.clientWidth, h: liveTextInner.clientHeight }
          : null,
      });
    }

    // Update userData to track the new content
    existingHTMLHotspot.userData.lastContent = hotspot.content;
    existingHTMLHotspot.userData.lastScale = hotspot.scale;
    this.applyTextAspectCompensation(element, contentType, {
      x: Number.isFinite((hotspot.scale as HotspotScale | undefined)?.x)
        ? ((hotspot.scale as HotspotScale).x as number)
        : 1,
      y: Number.isFinite((hotspot.scale as HotspotScale | undefined)?.y)
        ? ((hotspot.scale as HotspotScale).y as number)
        : 1,
    });
    this.applyHotspotStacking(hotspot.id);
  }

  public render(camera: THREE.Camera) {
    this.lastCamera = camera;
    if (this.baseFovDeg === null) {
      this.baseFovDeg = (camera as THREE.PerspectiveCamera).fov ?? 75;
    }
    // Ensure CSS3D renderer is properly initialized
    if (!this.css3dRenderer.domElement.parentElement && this.container) {
      this.initializeCSS3DRenderer();
    }

    if (this.css3dRenderer && this.css3dScene && camera) {
      // Apply render style per-hotspot
      const cameraPos = camera.position;
      Object.values(this.htmlHotspots).forEach((css3dObject) => {
        if (!css3dObject) return;
        const style = (css3dObject.userData.renderStyle as "billboard" | "3d") || this.renderStyle;
        if (style === "billboard") {
          css3dObject.lookAt(cameraPos);
          return;
        }
        const rotation = css3dObject.userData.lastRotation;
        if (rotation) {
          const facingQuat = new THREE.Quaternion();
          const tempObject = new THREE.Object3D();
          tempObject.position.copy(css3dObject.position);
          tempObject.lookAt(0, 0, 0);
          facingQuat.copy(tempObject.quaternion);

          const euler = new THREE.Euler(
            (rotation.x || 0) * (Math.PI / 180),
            (rotation.y || 0) * (Math.PI / 180),
            (rotation.z || 0) * (Math.PI / 180),
            "XYZ"
          );
          const rotationQuat = new THREE.Quaternion().setFromEuler(euler);
          css3dObject.quaternion.multiplyQuaternions(facingQuat, rotationQuat);
        } else {
          css3dObject.lookAt(0, 0, 0);
        }
      });

      Object.values(this.hotspotMeshes).forEach((mesh) => {
        if (!mesh?.userData?.isVideo) return;
        const style = (mesh.userData.renderStyle as "billboard" | "3d") || this.renderStyle;
        if (style === "billboard") {
          mesh.quaternion.copy(camera.quaternion);
          return;
        }
        const rotation = mesh.userData.lastRotation;
        if (rotation) {
          const facingQuat = new THREE.Quaternion();
          const tempObject = new THREE.Object3D();
          tempObject.position.copy(mesh.position);
          tempObject.lookAt(0, 0, 0);
          facingQuat.copy(tempObject.quaternion);

          const euler = new THREE.Euler(
            (rotation.x || 0) * (Math.PI / 180),
            (rotation.y || 0) * (Math.PI / 180),
            (rotation.z || 0) * (Math.PI / 180),
            "XYZ"
          );
          const rotationQuat = new THREE.Quaternion().setFromEuler(euler);
          mesh.quaternion.multiplyQuaternions(facingQuat, rotationQuat);
        } else {
          mesh.lookAt(0, 0, 0);
        }
      });

      // Render the CSS3D scene
      this.css3dRenderer.render(this.css3dScene, camera);
    }
  }

  private initializeCSS3DRenderer() {
    if (this.container && !this.css3dRenderer.domElement.parentElement) {
      // Set renderer size based on current container dimensions
      this.css3dRenderer.setSize(this.container.clientWidth, this.container.clientHeight);
      this.css3dRenderer.domElement.style.position = "absolute";
      this.css3dRenderer.domElement.style.top = "0";
      this.css3dRenderer.domElement.style.left = "0";
      this.css3dRenderer.domElement.style.pointerEvents = "none";
      // Keep hotspots behind skin components (skin overlay uses z-index: 2000).
      this.css3dRenderer.domElement.style.zIndex = String(HOTSPOT_LAYER_Z_INDEX);
      this.configureCSS3DLayeringMode();

      // Add CSS3D renderer to container
      this.container.appendChild(this.css3dRenderer.domElement);

      console.log("[HotspotManager] CSS3D renderer initialized successfully");
    }
  }

  private configureCSS3DLayeringMode() {
    const viewElement = this.css3dRenderer.domElement.firstElementChild as HTMLElement | null;
    const cameraElement = viewElement?.firstElementChild as HTMLElement | null;
    if (viewElement) {
      // CSS3D rendering needs preserve-3d in WebKit-based shells; forcing "flat"
      // can make hotspots disappear even though objects still exist in the scene.
      viewElement.style.transformStyle = "preserve-3d";
      viewElement.style.webkitTransformStyle = "preserve-3d";
    }
    if (cameraElement) {
      cameraElement.style.transformStyle = "preserve-3d";
      cameraElement.style.webkitTransformStyle = "preserve-3d";
    }
  }

  private requestSharpnessRerender(extraFrames = 2) {
    if (!this.lastCamera || !this.css3dRenderer || !this.css3dScene) return;
    let remaining = Math.max(0, extraFrames);
    const step = () => {
      if (!this.lastCamera || !this.css3dRenderer || !this.css3dScene) return;
      this.css3dRenderer.render(this.css3dScene, this.lastCamera);
      if (remaining > 0) {
        remaining -= 1;
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }

  public getHotspotMeshes() {
    return this.hotspotMeshes;
  }

  /** Returns the factor to multiply a user-specified CSS pixel value by so that
   *  it renders as approximately that many screen pixels in the CSS3DRenderer.
   *  Formula: distance / focalLength, where focalLength = height / (2·tan(FOV/2)).
   *  Uses a stable base FOV so zooming the camera does not resize hotspots.
   */
  private computePixelCompensation(distanceUnits: number): number {
    if (distanceUnits <= 0) return 1;
    const containerHeight = this.container?.clientHeight ?? 600;
    const fovDeg = this.baseFovDeg ?? 75;
    const fovRad = (fovDeg * Math.PI) / 180;
    const focalLength = containerHeight / (2 * Math.tan(fovRad / 2));
    return distanceUnits / focalLength;
  }

  /** Applies perspective compensation to a plain px value string.
   *  Non-px values (auto, %, em, rem …) are returned unchanged.
   */
  private applyPxCompensation(cssValue: string | null | undefined, compensation: number): string {
    if (!cssValue || cssValue === "auto") return cssValue ?? "auto";
    const match = /^(\d+(?:\.\d+)?)px$/.exec(cssValue);
    if (!match) return cssValue;
    return `${Math.max(1, Math.round(parseFloat(match[1]) * compensation))}px`;
  }

  private applyPaddingCompensation(padding: string, compensation: number): string {
    return padding
      .trim()
      .split(/\s+/)
      .map((part) => this.applyPxCompensation(part, compensation))
      .join(" ");
  }

  private applyHTMLHotspotPixelCompensation(hotspot: Hotspot, cssObject: CSS3DObject) {
    const element = cssObject.element as HTMLElement | undefined;
    if (!element) return;

    const contentWrapper = element.querySelector(":scope > .html-hotspot-content") as HTMLElement | null;
    if (!contentWrapper) return;

    const content = getHotspotContent(hotspot);
    const userWidth = resolveSizeValue(content?.width, "auto");
    const userHeight = resolveSizeValue(content?.height, "auto");
    const userFontSize = resolveSizeValue(content?.fontSize, "14px");
    const userPadding = resolveHotspotPadding(content);
    const compensation = this.computePixelCompensation(cssObject.position.length());

    if (userWidth && userWidth !== "auto") {
      element.dataset.userWidth = userWidth;
      contentWrapper.style.width = this.applyPxCompensation(userWidth, compensation);
    } else {
      delete element.dataset.userWidth;
      contentWrapper.style.width = userWidth || "auto";
    }

    if (userHeight && userHeight !== "auto") {
      element.dataset.userHeight = userHeight;
      contentWrapper.style.height = this.applyPxCompensation(userHeight, compensation);
    } else {
      delete element.dataset.userHeight;
      contentWrapper.style.height = userHeight || "auto";
    }

    element.dataset.userFontSize = userFontSize;
    contentWrapper.style.fontSize = this.applyPxCompensation(userFontSize, compensation);

    element.dataset.userPadding = userPadding;
    contentWrapper.style.padding = this.applyPaddingCompensation(userPadding, compensation);
  }

  public updateSize() {
    if (this.container && this.css3dRenderer) {
      this.css3dRenderer.setSize(this.container.clientWidth, this.container.clientHeight);
      // Reapply pixel compensation for all HTML hotspots because focalLength changed
      Object.entries(this.htmlHotspots).forEach(([hotspotId, cssObj]) => {
        const hotspot = useEditorStore.getState().hotspots.find((entry) => entry.id === hotspotId);
        if (!hotspot) return;
        this.applyHTMLHotspotPixelCompensation(hotspot, cssObj);
      });
    }
  }

  public setVideoPlaybackActive(active: boolean) {
    Object.values(this.hotspotMeshes).forEach((mesh) => {
      if (!mesh?.userData?.isVideo) return;
      const videoElement = mesh.userData.videoElement as HTMLVideoElement | undefined;
      if (!videoElement) return;
      if (!active) {
        try {
          videoElement.pause();
        } catch (_error) {
          // Ignore pause errors.
        }
        return;
      }

      if (videoElement.autoplay) {
        const playPromise = videoElement.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      }
    });
  }

  public setRenderingEnabled(enabled: boolean) {
    Object.values(this.hotspotMeshes).forEach((mesh) => {
      mesh.visible = enabled;
    });

    if (this.css3dRenderer?.domElement) {
      this.css3dRenderer.domElement.style.visibility = enabled ? "visible" : "hidden";
      this.css3dRenderer.domElement.style.opacity = enabled ? "1" : "0";
    }
  }

  public getHTMLHotspots() {
    return this.htmlHotspots;
  }

  public updateHotspotScaleImmediate(
    hotspotId: string,
    scale: { x: number; y: number; z?: number },
  ) {
    this.applyHotspotScale(hotspotId, scale);
  }

  public setTransformingState(isTransforming: boolean, hotspotId: string | null) {
    if (!hotspotId) return;
    const htmlHotspot = this.htmlHotspots[hotspotId];
    const element = htmlHotspot?.element as HTMLElement | undefined;
    if (!element) return;

    element.classList.toggle("hotspot-is-transforming", isTransforming);
    element.style.transition = isTransforming
      ? "none"
      : this.isPreviewMode
        ? "all 0.3s ease"
        : "none";
  }

  public setDraggingState(isDragging: boolean, hotspotId: string | null) {
    const targetHotspotId = hotspotId || this.draggedHotspotId;
    this.isDragging = isDragging;
    this.draggedHotspotId = hotspotId;

    this.setTransformingState(isDragging, targetHotspotId);
  }

  public updateHTMLHotspotPosition(
    hotspotId: string,
    position: { x: number; y: number; z: number },
  ) {
    const hotspot = this.htmlHotspots[hotspotId];
    if (hotspot) {
      const layeredPosition = this.applyLayerOffsetToPosition(
        new THREE.Vector3(position.x, position.y, position.z),
        hotspotId,
      );
      hotspot.position.copy(layeredPosition);
      hotspot.userData.lastPosition = position;
    }
  }

  cleanup() {
    // Clean up WebGL hotspots
    Object.values(this.hotspotMeshes).forEach((mesh) => {
      if (mesh.userData?.isVideo) {
        const videoElement = mesh.userData.videoElement as HTMLVideoElement | undefined;
        if (videoElement) {
          try {
            videoElement.pause();
            videoElement.removeAttribute("src");
            videoElement.load();
            const objectUrl = videoElement.dataset?.objectUrl;
            if (objectUrl) {
              URL.revokeObjectURL(objectUrl);
            }
          } catch (_error) {
            // Ignore cleanup errors.
          }
        }
        const videoTexture = mesh.userData.videoTexture as THREE.Texture | undefined;
        if (videoTexture) {
          videoTexture.dispose();
        }
      }
      if (mesh instanceof THREE.Mesh) {
        mesh.geometry.dispose();
        const mat = mesh.material;
        if (Array.isArray(mat)) {
          mat.forEach((m) => {
            m.dispose();
          });
        } else if (mat) {
          mat.dispose();
        }
      } else if (mesh instanceof THREE.Sprite) {
        const mat = mesh.material;
        if (mat) {
          mat.dispose();
        }
      }
    });
    this.hotspotMeshes = {};

    // Clean up HTML hotspots
    Object.values(this.htmlHotspots).forEach((css3dObject) => {
      if (css3dObject?.element) {
        this.css3dScene.remove(css3dObject);
        // CSS3D objects don't need explicit disposal like WebGL objects
      }
    });
    this.htmlHotspots = {};
    this.hotspotStackById.clear();

    // Clean up CSS3D renderer
    if (this.css3dRenderer?.domElement.parentElement) {
      this.css3dRenderer.domElement.parentElement.removeChild(this.css3dRenderer.domElement);
    }

    clearSVGTextureCache();
  }

  private selectHotspot(hotspot: Hotspot) {
    // Import store dynamically to avoid circular dependencies
    import("@/store/editorStore").then(({ useEditorStore }) => {
      const store = useEditorStore.getState();
      store.setSelectedHotspot(hotspot);
    });
  }

  private executeHotspotActions(hotspot: Hotspot) {
    const content = getHotspotContent(hotspot) || {};

    // Import store dynamically to avoid circular dependencies
    import("@/store/editorStore").then(({ useEditorStore }) => {
      const currentState = useEditorStore.getState();

      // Read actions array, with backward compat for old flat structure
      const actions = (content.actions as Array<Record<string, unknown>>) || [];

      if (actions.length > 0) {
        // New multi-action format
        actions.forEach((action) => {
          const type = action.type as string;

          if (type === "navigate" && action.linkedPanoramaId) {
            const linkedPanorama = currentState.panoramas?.find((p) => p.id === action.linkedPanoramaId);
            if (linkedPanorama) {
              const linkedPanoramaView = action.linkedPanoramaView as
                | { yaw: number; pitch: number; fov: number }
                | undefined;
              const transitionViewMode = action.linkTransitionViewMode as
                | "automatic"
                | "manual"
                | undefined;
              const transitionDirectionView = action.linkTransitionDirectionView as
                | { yaw: number; pitch: number; fov: number }
                | undefined;
              const rawTransitionType = action.linkTransitionType;
              const normalizedTransitionType =
                rawTransitionType === "same-view" ||
                rawTransitionType === "specific-view" ||
                rawTransitionType === "crossfade" ||
                rawTransitionType === "fade" ||
                rawTransitionType === "fade-same-view" ||
                rawTransitionType === "slide-to" ||
                rawTransitionType === "morph"
                  ? rawTransitionType
                  : rawTransitionType === "morph-1" ||
                      rawTransitionType === "morph-2" ||
                      rawTransitionType === "morph-3" ||
                      rawTransitionType === "morph-screen-space" ||
                      rawTransitionType === "morph-camera-driven"
                    ? "morph"
                    : linkedPanoramaView
                      ? "specific-view"
                      : "default";

              const sourcePanoramaId =
                currentState.selectedPanorama?.id ||
                (hotspot as { panorama_id?: string; panoramaId?: string }).panorama_id ||
                (hotspot as { panorama_id?: string; panoramaId?: string }).panoramaId;
              let hotspotPosition:
                | { x: number; y: number; z: number }
                | undefined;
              const rawHotspotPosition = hotspot.position;
              if (
                rawHotspotPosition &&
                typeof rawHotspotPosition === "object" &&
                "x" in rawHotspotPosition &&
                "y" in rawHotspotPosition &&
                "z" in rawHotspotPosition
              ) {
                hotspotPosition = rawHotspotPosition as { x: number; y: number; z: number };
              } else if (typeof rawHotspotPosition === "string") {
                try {
                  const parsedPosition = JSON.parse(rawHotspotPosition) as
                    | { x?: unknown; y?: unknown; z?: unknown }
                    | null;
                  if (parsedPosition && typeof parsedPosition === "object") {
                    const x = Number(parsedPosition.x);
                    const y = Number(parsedPosition.y);
                    const z = Number(parsedPosition.z);
                    if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
                      hotspotPosition = { x, y, z };
                    }
                  }
                } catch {
                  hotspotPosition = undefined;
                }
              }

              if (normalizedTransitionType === "slide-to") {
                warnSlideToDev("hotspotManager.executeHotspotActions", {
                  mode: this.isPreviewMode ? "preview" : "edit",
                  hotspotId: hotspot.id,
                  sourcePanoramaId,
                  targetPanoramaId: linkedPanorama.id,
                  transitionViewMode,
                  transitionDirectionView,
                  hotspotPosition,
                });
              }

              window.dispatchEvent(
                new CustomEvent("360crafter:navigate-panorama", {
                  detail: {
                    panoramaId: linkedPanorama.id,
                    transitionType: normalizedTransitionType,
                    linkedPanoramaView:
                      normalizedTransitionType === "same-view" ||
                      normalizedTransitionType === "fade-same-view"
                        ? currentState.currentCameraView || undefined
                        : normalizedTransitionType === "specific-view" ||
                            normalizedTransitionType === "morph"
                          ? linkedPanoramaView
                          : undefined,
                    hotspotPosition,
                    hotspotId: hotspot.id,
                    sourcePanoramaId,
                    fadeColor: action.linkTransitionFadeColor as string | undefined,
                    fadeDurationMs:
                      typeof action.linkTransitionFadeDurationSec === "number" &&
                      Number.isFinite(action.linkTransitionFadeDurationSec)
                        ? Math.max(0, action.linkTransitionFadeDurationSec) * 1000
                        : undefined,
                    transitionViewMode,
                    transitionDirectionView,
                  },
                }),
              );
            }
          } else if (type === "notification") {
            const title = (action.notificationTitle as string) || "Notification";
            const message = (action.notificationMessage as string) || "";
            const autoHideDuration = action.notificationAutoHideDuration as number | undefined;
            import("@/hooks/use-toast").then(({ toast }) => {
              toast({
                title,
                description: message,
                duration: autoHideDuration !== undefined ? autoHideDuration * 1000 : 1000000000,
                variant: autoHideDuration === undefined ? "alert" : "default",
              });
            });
          } else if (type === "download" && action.downloadFileUrl) {
            void promptAndSaveFile(action.downloadFileUrl as string, action.downloadFileName as string | undefined);
          } else if (type === "showHideSkin") {
            const targetIds = (action.skinVisibilityTargetComponentIds as string[]) || [];
            const visAction = action.skinVisibilityAction as string;
            if (visAction && targetIds.length > 0) {
              if (visAction === "show") currentState.showSkinComponents(targetIds);
              else if (visAction === "hide") currentState.hideSkinComponents(targetIds);
              else if (visAction === "toggle") currentState.toggleSkinComponents(targetIds);
            }
          } else if (type === "showHideHotspots") {
            let targetIds = (action.hotspotVisibilityTargetIds as string[]) || [];
            const config = action.hotspotVisibilityItemsConfig as Record<string, unknown>;
            if (config?.mode === "dynamic" && targetIds.length === 0) {
              targetIds = currentState.hotspots.map((h) => h.id);
            }
            const visAction = action.hotspotVisibilityAction as string;
            if (visAction && targetIds.length > 0) {
              if (visAction === "show") currentState.showHotspots(targetIds);
              else if (visAction === "hide") currentState.hideHotspots(targetIds);
              else if (visAction === "toggle") currentState.toggleHotspots(targetIds);
            }
          }
        });
      } else {
        // Backward compat: old flat structure
        let actionsTriggered = 0;

        // Handle panorama linking if hotspot has one
        const linkedPanoramaId = content.linkedPanoramaId;
        if (linkedPanoramaId && currentState.panoramas) {
          const linkedPanorama = currentState.panoramas.find((p) => p.id === linkedPanoramaId);
          if (linkedPanorama) {
            const linkedPanoramaView = content.linkedPanoramaView as
              | { yaw: number; pitch: number; fov: number }
              | undefined;
            const transitionViewMode = content.linkTransitionViewMode as
              | "automatic"
              | "manual"
              | undefined;
            const transitionDirectionView = content.linkTransitionDirectionView as
              | { yaw: number; pitch: number; fov: number }
              | undefined;
            const rawTransitionType = content.linkTransitionType;
            const normalizedTransitionType =
              rawTransitionType === "same-view" ||
              rawTransitionType === "specific-view" ||
              rawTransitionType === "crossfade" ||
              rawTransitionType === "slide-to" ||
              rawTransitionType === "morph"
                ? rawTransitionType
                : rawTransitionType === "morph-1" ||
                    rawTransitionType === "morph-2" ||
                    rawTransitionType === "morph-3" ||
                    rawTransitionType === "morph-screen-space" ||
                    rawTransitionType === "morph-camera-driven"
                  ? "morph"
                  : linkedPanoramaView
                    ? "specific-view"
                    : "default";

            const sourcePanoramaId =
              currentState.selectedPanorama?.id ||
              (hotspot as { panorama_id?: string; panoramaId?: string }).panorama_id ||
              (hotspot as { panorama_id?: string; panoramaId?: string }).panoramaId;
            let hotspotPosition:
              | { x: number; y: number; z: number }
              | undefined;
            const rawHotspotPosition = hotspot.position;
            if (
              rawHotspotPosition &&
              typeof rawHotspotPosition === "object" &&
              "x" in rawHotspotPosition &&
              "y" in rawHotspotPosition &&
              "z" in rawHotspotPosition
            ) {
              hotspotPosition = rawHotspotPosition as { x: number; y: number; z: number };
            } else if (typeof rawHotspotPosition === "string") {
              try {
                const parsedPosition = JSON.parse(rawHotspotPosition) as
                  | { x?: unknown; y?: unknown; z?: unknown }
                  | null;
                if (parsedPosition && typeof parsedPosition === "object") {
                  const x = Number(parsedPosition.x);
                  const y = Number(parsedPosition.y);
                  const z = Number(parsedPosition.z);
                  if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
                    hotspotPosition = { x, y, z };
                  }
                }
              } catch {
                hotspotPosition = undefined;
              }
            }

            if (normalizedTransitionType === "slide-to") {
              warnSlideToDev("hotspotManager.executeHotspotActions.legacy", {
                mode: this.isPreviewMode ? "preview" : "edit",
                hotspotId: hotspot.id,
                sourcePanoramaId,
                targetPanoramaId: linkedPanorama.id,
                transitionViewMode,
                transitionDirectionView,
                hotspotPosition,
              });
            }

            window.dispatchEvent(
              new CustomEvent("360crafter:navigate-panorama", {
                detail: {
                  panoramaId: linkedPanorama.id,
                  transitionType: normalizedTransitionType,
                    linkedPanoramaView:
                      normalizedTransitionType === "same-view"
                        ? currentState.currentCameraView || undefined
                        : normalizedTransitionType === "specific-view" ||
                            normalizedTransitionType === "morph"
                          ? linkedPanoramaView
                          : undefined,
                    hotspotPosition,
                    hotspotId: hotspot.id,
                    sourcePanoramaId,
                    transitionViewMode,
                    transitionDirectionView,
                  },
                }),
              );
            actionsTriggered++;
          }
        }

        // Show notification if hotspot has one
        const notificationMessage = content.notificationMessage;
        const notificationTitle = content.notificationTitle;
        if (notificationMessage || notificationTitle) {
          const title = notificationTitle || "Notification";
          const message = notificationMessage || "";
          const autoHideDuration = content.notificationAutoHideDuration;
          const isAlert = autoHideDuration === undefined;

          // Import toast dynamically to avoid circular dependencies
          import("@/hooks/use-toast").then(({ toast }) => {
            toast({
              title: title,
              description: message,
              duration: autoHideDuration !== undefined ? autoHideDuration * 1000 : 1000000000,
              variant: isAlert ? "alert" : "default",
            });
          });
          actionsTriggered++;
        }

        // Trigger download if hotspot has one
        const downloadFileUrl = content.downloadFileUrl;
        if (downloadFileUrl) {
          void promptAndSaveFile(downloadFileUrl, content.downloadFileName as string | undefined);
          actionsTriggered++;
        }

        // Handle skin component visibility actions
        const skinVisibilityTargetIds = content.skinVisibilityTargetComponentIds;
        const skinVisibilityAction = content.skinVisibilityAction;
        if (skinVisibilityTargetIds && skinVisibilityTargetIds.length > 0 && skinVisibilityAction) {
          if (skinVisibilityAction === "show") {
            currentState.showSkinComponents(skinVisibilityTargetIds);
          } else if (skinVisibilityAction === "hide") {
            currentState.hideSkinComponents(skinVisibilityTargetIds);
          } else if (skinVisibilityAction === "toggle") {
            currentState.toggleSkinComponents(skinVisibilityTargetIds);
          }
          actionsTriggered++;
        }

        // Handle hotspot visibility actions
        const hotspotVisibilityTargetIds = content.hotspotVisibilityTargetIds;
        const hotspotVisibilityAction = content.hotspotVisibilityAction;
        const hotspotVisibilityConfig = content.hotspotVisibilityItemsConfig;

        if (hotspotVisibilityAction) {
          // Determine which hotspots to apply the action to
          let targetIds = hotspotVisibilityTargetIds || [];

          // In dynamic mode with filters/sort, apply action to all hotspots
          // (In a future update, we'd apply filters, but for now we use all available)
          if (hotspotVisibilityConfig?.mode === "dynamic" && targetIds.length === 0) {
            targetIds = currentState.hotspots.map((h) => h.id);
          }

          if (targetIds.length > 0) {
            if (hotspotVisibilityAction === "show") {
              currentState.showHotspots(targetIds);
            } else if (hotspotVisibilityAction === "hide") {
              currentState.hideHotspots(targetIds);
            } else if (hotspotVisibilityAction === "toggle") {
              currentState.toggleHotspots(targetIds);
            }
            actionsTriggered++;
          }
        }

        // Handle confetti actions
        const confettiAction = content.confettiAction;
        if (confettiAction) {
          // Import confetti service dynamically
          import("@/utils/confettiService").then(({ confettiService }) => {
            confettiService.triggerConfetti(confettiAction);
          });
          actionsTriggered++;
        }

        if (actionsTriggered === 0) {
          console.log("No actions configured for hotspot:", hotspot.id);
        }
      }

      // Handle confetti (independent, always additive)
      const confettiAction = content.confettiAction;
      if (confettiAction) {
        import("@/utils/confettiService").then(({ confettiService }) => {
          confettiService.triggerConfetti(confettiAction);
        });
      }
    });
  }
}
